import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../Layout/Layout';
import CollabMeetingRoom from './CollabMeetingRoom';
import { useUser } from '../../contexts/UserContext';
import * as meetingService from '../../services/meetingService';
import * as providerService from '../../services/providerService';
import {
    MEETING_TYPE_OPTIONS,
    formatMeetingDate,
    formatStartTimeOnly,
    getMeetingPlatformLabel,
    toDateKey,
    buildMonthGrid,
    calculateDuration,
    parseGuestEmails,
    buildLinkModeDateTimes,
    getEnovaMeetingLink,
    getMeetingShareLink
} from '../../utils/dashboardUtils';

const MeetingTypeSelect = ({ value, onChange, className, placeholder = 'Select meeting type' }) => (
    <div className="relative w-full">
        <select
            value={value}
            onChange={onChange}
            className={`${className || ''} appearance-none pr-9`}
        >
            <option value="">{placeholder}</option>
            {MEETING_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
        </select>
        <svg
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
            aria-hidden="true"
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    </div>
);

const CollabMeetingDashboard = () => {
    const { user, setUser } = useUser();
    const [meetings, setMeetings] = useState([]);
    const [guestName, setGuestName] = useState('');
    const [guestEmailInput, setGuestEmailInput] = useState('');
    const [guestMeetingTitle, setGuestMeetingTitle] = useState('');
    const [guestMeetingCreator, setGuestMeetingCreator] = useState('');
    const [guestLoading, setGuestLoading] = useState(false);
    const [guestError, setGuestError] = useState('');
    const [checkingGuestMeeting, setCheckingGuestMeeting] = useState(true);
    const [roomId, setRoomId] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [activeTab, setActiveTab] = useState('upcoming');
    const [searchTerm, setSearchTerm] = useState('');
    const [meetingsViewMode, setMeetingsViewMode] = useState('list'); // 'list' | 'calendar'
    const [calendarCursor, setCalendarCursor] = useState(() => {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth(), 1);
    });
    const [selectedCalendarDay, setSelectedCalendarDay] = useState(() => {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    });
    const [calendarDayModal, setCalendarDayModal] = useState({ show: false, date: null, meetings: [] });
    const [showCalendarMonthPicker, setShowCalendarMonthPicker] = useState(false);
    const [pickerYear, setPickerYear] = useState(() => new Date().getFullYear());
    const calendarMonthPickerRef = useRef(null);
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);
    useEffect(() => {
        if (showCalendarMonthPicker) {
            setPickerYear(calendarCursor.getFullYear());
        }
    }, [showCalendarMonthPicker, calendarCursor]);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (calendarMonthPickerRef.current && !calendarMonthPickerRef.current.contains(event.target)) {
                setShowCalendarMonthPicker(false);
            }
        };
        if (showCalendarMonthPicker) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showCalendarMonthPicker]);
    const handleGuestJoinSubmit = async (e) => {
        e.preventDefault();
        if (!guestName.trim()) {
            setGuestError("Please enter your name to join the meeting.");
            return;
        }

        if (!guestEmailInput.trim()) {
            setGuestError("Please enter your email address to join the meeting.");
            return;
        }

        setGuestLoading(true);
        setGuestError('');
        try {
            const response = await api.post('/collab-meetings/guest-join', {
                name: guestName.trim(),
                email: guestEmailInput.trim(),
                meeting_id: roomId
            });
            if (response.data && response.data.status === 'success') {
                const { user: guestUser } = response.data;
                localStorage.removeItem('token');
                setUser(guestUser);
                setActiveMeeting({ id: roomId, name: guestMeetingTitle || 'Collaborative Meeting' });
            }

        } catch (err) {
            setGuestError(err.response?.data?.messages?.error || err.response?.data?.error || err.response?.data?.message || "Failed to join the meeting as guest.");
        } finally {
            setGuestLoading(false);
        }

    };
    const [visibleCount, setVisibleCount] = useState(4);
    const [loading, setLoading] = useState(true);
    const [activeMeeting, setActiveMeeting] = useState(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const rId = params.get('room') || params.get('meeting_id') || params.get('id') || params.get('meeting') || sessionStorage.getItem('active_meeting_id');
            if (rId) {
                let cleanId = rId.replace(/[^0-9]/g, '') || rId;
                if (cleanId) {
                    const storedTitle = localStorage.getItem(`meeting_title_${cleanId}`) || 'Collaborative Meeting';
                    return { id: cleanId, name: storedTitle };
                }
            }
        } catch (e) {
            console.error("Error initializing activeMeeting state:", e);
        }
        return null;
    });
        const [meetingCodeInput, setMeetingCodeInput] = useState('');
        const [joiningMeeting, setJoiningMeeting] = useState(false);
        // Dropdown and modal states

        const [dropdownOpen, setDropdownOpen] = useState(false);
        const [showLaterModal, setShowLaterModal] = useState(false);
        const [showCreateLaterTargetModal, setShowCreateLaterTargetModal] = useState(false);
        const [meetingCreationMode, setMeetingCreationMode] = useState('later'); // 'later' or 'instant' or 'link'
        const [createLaterMeetingType, setCreateLaterMeetingType] = useState('');
        const [createLaterShowEmployees, setCreateLaterShowEmployees] = useState(false);
        const [createLaterShowCustomers, setCreateLaterShowCustomers] = useState(false);
        const [createLaterShowExternal, setCreateLaterShowExternal] = useState(false);
        const [employeePickerOpen, setEmployeePickerOpen] = useState(false);
        const [creatingProviderLink, setCreatingProviderLink] = useState(false);
        const [createLaterDate, setCreateLaterDate] = useState('');
        const [createLaterTime, setCreateLaterTime] = useState('');
        const [createMeetingTitle, setCreateMeetingTitle] = useState('');
        const [createdMeetingLink, setCreatedMeetingLink] = useState('');
        const [inviteEmail, setInviteEmail] = useState('');
        const [isSendingInvite, setIsSendingInvite] = useState(false);
        const [viewAttendanceModal, setViewAttendanceModal] = useState({ show: false, meeting: null, participants: [] });
        const [attendancePage, setAttendancePage] = useState(1);
        const [attendanceSortBy, setAttendanceSortBy] = useState('host');
        const [attendancePerPage, setAttendancePerPage] = useState(5);
        const [dashboardPage, setDashboardPage] = useState(1);
        const [createdMeetingId, setCreatedMeetingId] = useState('');
        const [laterModalNote, setLaterModalNote] = useState('');
        const [showCalendarModal, setShowCalendarModal] = useState(false);
        // Custom dialog/popup states

        const [alertPopup, setAlertPopup] = useState({ show: false, message: '', title: 'Notification' });
        const [confirmPopup, setConfirmPopup] = useState({ show: false, message: '', title: 'Confirm', onConfirm: null });
        const [resolutionPopup, setResolutionPopup] = useState({ show: false, escalationId: null, notes: '' });
        const [editMeetingModal, setEditMeetingModal] = useState({
            show: false,
            meetingId: null,
            title: '',
            date: '',
            startTime: '',
            endTime: '',
            errors: {}
        });
        // Calendar scheduling states

        const [spaces, setSpaces] = useState([]);
        const [showScheduleFormModal, setShowScheduleFormModal] = useState(false);
        const [scheduleTitle, setScheduleTitle] = useState('');
        const [scheduleDate, setScheduleDate] = useState('');
        const [scheduleStartTime, setScheduleStartTime] = useState('16:00');
        const [scheduleEndTime, setScheduleEndTime] = useState('17:00');
        const [scheduleType, setScheduleType] = useState('online');
        const [scheduleSpaceId, setScheduleSpaceId] = useState(1);
        const [scheduleDescription, setScheduleDescription] = useState('');
        const [scheduleLocation, setScheduleLocation] = useState('');
        const [scheduleNotification, setScheduleNotification] = useState(30);
        const [scheduleBusy, setScheduleBusy] = useState(true);
        const [scheduleVisibility, setScheduleVisibility] = useState('default');
        const [scheduleGuests, setScheduleGuests] = useState('');
        const [scheduleBuilding, setScheduleBuilding] = useState('');
        const [scheduleFloor, setScheduleFloor] = useState('');
        const [scheduleRoomName, setScheduleRoomName] = useState('Alpha Suite');
        const [scheduleSeatingCapacity, setScheduleSeatingCapacity] = useState('12');
        const [scheduleExternalMeetingUrl, setScheduleExternalMeetingUrl] = useState('');
        const [scheduleMeetingPasscode, setScheduleMeetingPasscode] = useState('');
        const [scheduleRecurring, setScheduleRecurring] = useState('none');
        const [scheduleAdmissionControl, setScheduleAdmissionControl] = useState('admit'); // 'admit' (Requires Host Admit) or 'direct' (Direct Join)
        const [scheduleFormErrors, setScheduleFormErrors] = useState({});
        const [targetModalErrors, setTargetModalErrors] = useState({});
        // Attendance & target visibility states

        const [targetType, setTargetType] = useState(['all_employees']);
        const [allEmployeesList, setAllEmployeesList] = useState([]);
        const [selectedEmployees, setSelectedEmployees] = useState([]);
        const [allCustomersList, setAllCustomersList] = useState([]);
        const [selectedCustomers, setSelectedCustomers] = useState([]);
        const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
        const [customerSearchQuery, setCustomerSearchQuery] = useState('');
        const [manualEmails, setManualEmails] = useState('');
        // Provider account management states

        const [scheduleAutoSchedule, setScheduleAutoSchedule] = useState(true);
        const [providerAvailability, setProviderAvailability] = useState(null);
        const [providerAccounts, setProviderAccounts] = useState([]);
        const [escalations, setEscalations] = useState([]);
        const [showProviderAdmin, setShowProviderAdmin] = useState(false);
        const [providerFormData, setProviderFormData] = useState({
            provider: 'zoom', account_label: '', email: '', client_id: '', client_secret: '',
            account_id: '', api_key: '', api_secret: '', refresh_token: '',
            max_concurrent: 1, subscription_tier: 'pro', notes: ''
        });
        const [showProviderForm, setShowProviderForm] = useState(false);
        const [editingProviderId, setEditingProviderId] = useState(null);
        const [providerTestResult, setProviderTestResult] = useState(null);
        const [providerError, setProviderError] = useState('');
        const dropdownRef = useRef(null);
        const prevActiveMeetingRef = useRef(null);
        const fetchEmployees = useCallback(async () => {
            try {
                const data = await meetingService.fetchEmployees();
                if (data && data.status === 'success') {
                    setAllEmployeesList(data.data || []);
                }
            } catch (error) {
                console.error('Error fetching employees:', error);
            }
        }, []);

        const fetchCustomers = useCallback(async () => {
            try {
                const data = await meetingService.fetchCustomers();
                if (data && data.status === 'success') {
                    setAllCustomersList(data.data || []);
                }
            } catch (error) {
                console.error('Error fetching customers:', error);
            }
        }, []);

        useEffect(() => {
            if (showScheduleFormModal || showCreateLaterTargetModal) {
                fetchEmployees();
                fetchCustomers();
            }
        }, [showScheduleFormModal, showCreateLaterTargetModal, fetchEmployees, fetchCustomers]);

        // Check availability dynamically when date/time or type changes
        useEffect(() => {
            if (!showScheduleFormModal || !scheduleAutoSchedule) return;
            if (scheduleType !== 'hybrid_zoom' && scheduleType !== 'hybrid_google_meet') return;
            const checkAvailability = async () => {
                try {
                    const startStr = `${scheduleDate} ${scheduleStartTime}:00`;
                    let endStr = '';
                    if (!scheduleEndTime) {
                        const startDt = new Date(`${scheduleDate} ${scheduleStartTime}:00`);
                        if (!isNaN(startDt.getTime())) {
                            startDt.setHours(startDt.getHours() + 1);
                            const year = startDt.getFullYear();
                            const month = String(startDt.getMonth() + 1).padStart(2, '0');
                            const day = String(startDt.getDate()).padStart(2, '0');
                            const hours = String(startDt.getHours()).padStart(2, '0');
                            const minutes = String(startDt.getMinutes()).padStart(2, '0');
                            endStr = `${year}-${month}-${day} ${hours}:${minutes}:00`;
                        }
                    } else {
                        endStr = `${scheduleDate} ${scheduleEndTime}:00`;
                    }
                    const provider = scheduleType === 'hybrid_zoom' ? 'zoom' : 'google_meet';
                    const data = await providerService.checkProviderAvailability(provider, startStr, endStr);
                    if (data && data.status === 'success') {
                        setProviderAvailability(data.data);
                    }
                } catch (err) {
                    console.error('Failed to check provider availability:', err);
                }
            };
            if (scheduleDate && scheduleStartTime) {
                checkAvailability();
            }
        }, [scheduleDate, scheduleStartTime, scheduleEndTime, scheduleType, scheduleAutoSchedule, showScheduleFormModal]);

        const fetchProviderAccounts = useCallback(async () => {
            try {
                const data = await providerService.fetchProviderAccounts();
                if (data?.status === 'success') {
                    setProviderAccounts(data.data);
                }
            } catch (err) {
                console.error('Failed to fetch provider accounts:', err);
            }
        }, []);

        const fetchEscalations = useCallback(async () => {
            try {
                const data = await providerService.fetchEscalations();
                if (data?.status === 'success') {
                    setEscalations(data.data);
                }
            } catch (err) {
                console.error('Failed to fetch escalations:', err);
            }
        }, []);

        const handleSaveProviderAccount = async () => {
            setProviderError('');
            try {
                await providerService.saveProviderAccount(providerFormData, editingProviderId);
                setShowProviderForm(false);
                setEditingProviderId(null);
                fetchProviderAccounts();
            } catch (err) {
                setProviderError('Failed to save provider account: ' + (err.response?.data?.messages?.error || err.response?.data?.message || err.message));
            }
        };

        const handleDeleteProviderAccount = (id) => {
            setProviderError('');
            setConfirmPopup({
                show: true,
                title: 'Delete Account',
                message: 'Are you sure you want to delete this provider account?',
                onConfirm: async () => {
                    try {
                        await providerService.deleteProviderAccount(id);
                        fetchProviderAccounts();
                    } catch (err) {
                        setProviderError('Failed to delete account: ' + (err.response?.data?.message || err.message));
                    }
                }
            });
        };

        const handleTestProviderAccount = async (id) => {
            setProviderError('');
            try {
                const data = await providerService.testProviderAccount(id);
                const payload = data?.data || data;
                if ((data?.status === 'success' || payload?.success || payload?.valid) && payload?.valid !== false) {
                    setProviderTestResult({
                        valid: true,
                        message: payload?.message || 'Connection test successful.'
                    });
                } else {
                    const errorMsg = payload?.message || data?.message || 'Connection test failed.';
                    setProviderError(errorMsg);
                    setProviderTestResult({
                        valid: false,
                        message: errorMsg
                    });
                }
            } catch (err) {
                const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Connection test failed.';
                setProviderError('Test failed: ' + errorMsg);
                setProviderTestResult({
                    valid: false,
                    message: 'Test failed: ' + errorMsg
                });
            } finally {
                fetchProviderAccounts();
            }
            setTimeout(() => setProviderTestResult(null), 6000);
        };

        const handleResolveEscalation = (id) => {
            setResolutionPopup({ show: true, escalationId: id, notes: '' });
        };

        const submitEscalationResolution = async () => {
            const { escalationId, notes } = resolutionPopup;
            if (!notes.trim()) {
                setProviderError('Please enter resolution notes before submitting.');
                return;
            }

            try {
                await providerService.resolveEscalation(escalationId, notes);
                setResolutionPopup({ show: false, escalationId: null, notes: '' });
                fetchEscalations();
            } catch (err) {
                setProviderError('Failed to resolve escalation: ' + (err.response?.data?.message || err.message));
            }
        };

        const handleEditMeeting = async (e, meeting) => {
            e.stopPropagation();
            
            const isNotEditable = (meeting.status && meeting.status !== 'scheduled');
            if (isNotEditable) {
                setAlertPopup({
                    show: true,
                    title: 'Not Editable',
                    message: 'This meeting is in progress or completed and cannot be edited.'
                });
                return;
            }
            
            const startDt = new Date(meeting.start_time.replace(/-/g, "/"));
            const yr = startDt.getFullYear();
            const mo = String(startDt.getMonth() + 1).padStart(2, '0');
            const dy = String(startDt.getDate()).padStart(2, '0');
            const hr = String(startDt.getHours()).padStart(2, '0');
            const mn = String(startDt.getMinutes()).padStart(2, '0');
            
            const datePart = `${yr}-${mo}-${dy}`;
            const startTimePart = `${hr}:${mn}`;

            let currentEmployees = [];
            let currentGuestEmails = [];
            try {
                const data = await meetingService.fetchParticipants(meeting.id);
                if (data && data.status === 'success') {
                    const parts = data.data || [];
                    parts.forEach(p => {
                        if (p.user_id) currentEmployees.push(Number(p.user_id));
                        if (p.guest_email) currentGuestEmails.push(p.guest_email);
                    });
                }
            } catch (err) {
                console.error("Failed to load meeting participants", err);
            }

            setEditMeetingModal({
                show: true,
                meetingId: meeting.id,
                title: meeting.title || '',
                date: datePart,
                startTime: startTimePart,
                selectedEmployees: currentEmployees,
                manualEmails: currentGuestEmails.join(', '),
                searchEmpQuery: '',
                errors: {}
            });
        };

        const handleViewAttendance = async (meeting) => {
            try {
                setAttendancePage(1);
                const data = await meetingService.fetchAttendance(meeting.id);
                if (data && data.status === 'success') {
                    setViewAttendanceModal({
                        show: true,
                        meeting,
                        participants: data.data || []
                    });
                }
            } catch (err) {
                console.error("Failed to load attendance", err);
            }
        };

        const submitEditMeeting = async () => {
            const errors = {};
            if (!editMeetingModal.title.trim()) {
                errors.title = 'Title is required.';
            }
            if (!editMeetingModal.date) {
                errors.date = 'Date is required.';
            }
            if (!editMeetingModal.startTime) {
                errors.startTime = 'Start time is required.';
            }

            const startDateTimeStr = `${editMeetingModal.date} ${editMeetingModal.startTime}:00`;

            if (Object.keys(errors).length > 0) {
                setEditMeetingModal(prev => ({ ...prev, errors }));
                return;
            }

            try {
                await meetingService.updateMeeting(editMeetingModal.meetingId, {
                    title: editMeetingModal.title,
                    start_time: startDateTimeStr,
                    selected_employees: editMeetingModal.selectedEmployees || [],
                    manual_emails: editMeetingModal.manualEmails || ''
                });

                setEditMeetingModal({
                    show: false,
                    meetingId: null,
                    title: '',
                    date: '',
                    startTime: '',
                    selectedEmployees: [],
                    manualEmails: '',
                    searchEmpQuery: '',
                    errors: {}
                });

                setAlertPopup({
                    show: true,
                    title: 'Success',
                    message: 'Meeting details & participants updated successfully.'
                });

                fetchMeetings();
            } catch (err) {
                console.error('Failed to update meeting details:', err);
                setAlertPopup({
                    show: true,
                    title: 'Error',
                    message: err.response?.data?.message || 'Failed to update meeting details.'
                });
            }
        };
        useEffect(() => {
            if (showProviderAdmin) {
                fetchProviderAccounts();
                fetchEscalations();
            }

        }, [showProviderAdmin]);
        // Fetch rooms dynamically on load

        useEffect(() => {
            const params = new URLSearchParams(window.location.search);
            const rId = params.get('room') || params.get('meeting_id') || params.get('id') || params.get('meeting');
            setRoomId(rId);
            if (!user) {
                const token = localStorage.getItem('token');
                if (token) {
                    // User profile is still loading from UserProvider. Wait for it.
                    return;
                }

                if (!rId) {
                    // No user, no token and no room => redirect to login
                    window.location.href = '/';
                    return;
                }

                // Guest mode: fetch meeting info publicly
                const checkMeeting = async () => {
                    try {
                        let cleanId = rId.replace(/[^0-9]/g, '') || rId;
                        const data = await meetingService.fetchGuestMeeting(cleanId);
                        if (data && data.status === 'success') {
                            setGuestMeetingTitle(data.data.title);
                            setGuestMeetingCreator(data.data.creator_name || '');
                        } else {
                            setGuestError("Meeting not found or already ended.");
                        }
                    } catch (err) {
                        setGuestError("Meeting not found or already ended.");
                    } finally {
                        setCheckingGuestMeeting(false);
                    }
                };
                checkMeeting();
                setLoading(false);
                return;
            }

            // Authenticated user mode: Auto-refresh meetings list silently every 5s so completed meetings automatically move to Previous tab
            fetchMeetings();
            fetchSpaces();

            const dashboardSyncInterval = setInterval(() => {
                fetchMeetings(true);
            }, 5000);

            if (rId) {
                // Auto-join meeting directly if room/meeting ID is present in URL
                let cleanId = rId.replace(/[^0-9]/g, '') || rId;
                if (cleanId) {
                    // Set active meeting state immediately so refresh (F5) re-enters room instantly
                    const storedTitle = localStorage.getItem(`meeting_title_${cleanId}`) || 'Collaborative Meeting';
                    setActiveMeeting({ id: cleanId, name: storedTitle });
                    sessionStorage.setItem('active_meeting_id', cleanId);

                    const autoJoin = async () => {
                        setJoiningMeeting(true);
                        try {
                            const data = await meetingService.fetchMeeting(cleanId);
                            if (data && data.status === 'success') {
                                const meeting = data.data;
                                if (meeting.status !== 'completed' && meeting.status !== 'cancelled') {
                                    setMeetingCodeInput(rId);
                                    setActiveMeeting({ id: meeting.id, name: meeting.title });
                                    localStorage.setItem(`meeting_title_${meeting.id}`, meeting.title);
                                } else {
                                    setActiveMeeting(null);
                                    sessionStorage.removeItem('active_meeting_id');
                                    window.history.pushState({}, '', window.location.pathname);
                                    setMeetingCodeInput('');
                                }
                            }
                        } catch (error) {
                            console.error("Auto-join failed:", error);
                        } finally {
                            setJoiningMeeting(false);
                        }
                    };
                    autoJoin();
                }
            }

            return () => clearInterval(dashboardSyncInterval);
        }, [user]);

        // Sync activeMeeting state with browser URL search parameters for refresh stability
        useEffect(() => {
            const params = new URLSearchParams(window.location.search);
            const currentRoomId = params.get('room') || params.get('meeting_id') || params.get('id');
            if (activeMeeting) {
                sessionStorage.setItem('active_meeting_id', activeMeeting.id);
                if (currentRoomId !== String(activeMeeting.id)) {
                    const newUrl = `${window.location.pathname}?room=${activeMeeting.id}`;
                    window.history.pushState({ room: activeMeeting.id }, '', newUrl);
                }
            } else {
                // Only clear sessionStorage & URL parameters if user was previously in an active meeting and left
                if (prevActiveMeetingRef.current !== null) {
                    sessionStorage.removeItem('active_meeting_id');
                    if (currentRoomId) {
                        setMeetingCodeInput('');
                        window.history.pushState({}, '', window.location.pathname);
                    }
                }
            }
            prevActiveMeetingRef.current = activeMeeting;
        }, [activeMeeting]);
        // Close dropdown on outside click

        useEffect(() => {
            const handleClickOutside = (event) => {
                if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                    setDropdownOpen(false);
                }

            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }, []);
        const fetchSpaces = useCallback(async () => {
            try {
                const data = await meetingService.fetchSpaces();
                if (data && data.status === 'success') {
                    setSpaces(data.data || []);
                }
            } catch (error) {
                console.error('Error fetching collab spaces:', error);
            }
        }, []);

        const fetchMeetings = useCallback(async (isBackground = false) => {
            if (!isBackground) setLoading(true);
            try {
                const data = await meetingService.fetchMeetings();
                if (data && data.status === 'success') {
                    setMeetings(data.data || []);
                }
            } catch (error) {
                console.error('Error fetching collab meetings:', error);
            } finally {
                if (!isBackground) setLoading(false);
            }
        }, []);

        useEffect(() => {
            fetchMeetings();
            fetchSpaces();

            const intervalId = setInterval(() => {
                fetchMeetings(true);
            }, 10000);

            const handleFocus = () => {
                fetchMeetings(true);
            };

            window.addEventListener('focus', handleFocus);
            return () => {
                clearInterval(intervalId);
                window.removeEventListener('focus', handleFocus);
            };
        }, [fetchMeetings, fetchSpaces]);

        const handleDeleteMeeting = useCallback(async (e, meetingId) => {
            if (e) e.stopPropagation();
            setConfirmPopup({
                show: true,
                title: "Delete Meeting Room",
                confirmText: "Delete",
                message: "Are you sure you want to delete this meeting room for all users?",
                onConfirm: async () => {
                    try {
                        const data = await meetingService.deleteMeeting(meetingId);
                        if (data && data.status === 'success') {
                            fetchMeetings();
                        }
                    } catch (error) {
                        console.error('Failed to delete meeting:', error);
                        setAlertPopup({
                            show: true,
                            title: 'Error',
                            message: error.response?.data?.messages?.error || 'Failed to delete meeting room.'
                        });
                    }
                }
            });
        }, [fetchMeetings]);

        const handleEndMeeting = useCallback((meetingId, e) => {
            if (e) e.stopPropagation();
            setConfirmPopup({
                show: true,
                title: "End Meeting",
                confirmText: "End",
                message: "Are you sure you want to end this meeting? It will be moved to Previous meetings.",
                onConfirm: async () => {
                    try {
                        const data = await meetingService.endMeeting(meetingId);
                        if (data && data.status === 'success') {
                            setActiveMeeting(null);
                            sessionStorage.removeItem('active_meeting_id');
                            window.history.pushState({}, '', window.location.pathname);
                            setActiveTab('previous');
                            fetchMeetings();
                        }
                    } catch (error) {
                        console.error('Failed to end meeting:', error);
                        setAlertPopup({
                            show: true,
                            title: 'Error',
                            message: error.response?.data?.message || 'Failed to end meeting.'
                        });
                    }
                }
            });
        }, [fetchMeetings]);
        // Google Meet style: Create a meeting for later

        const parseGuestEmails = (value) => {
            if (!value) return [];
            if (Array.isArray(value)) {
                return value
                    .flatMap((item) => parseGuestEmails(item))
                    .filter(Boolean);
            }
            return String(value)
                .split(/[,;\n\r]+/)
                .map((e) => e.trim())
                .filter((e) => e && e.includes('@') && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
        };

        const buildLinkModeDateTimes = () => {
            const now = new Date();
            const y = now.getFullYear();
            const m = String(now.getMonth() + 1).padStart(2, '0');
            const d = String(now.getDate()).padStart(2, '0');
            const timePart = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:00`;
            const start_time = `${y}-${m}-${d} ${timePart}`;
            const later = new Date(now.getTime() + 60 * 60 * 1000);
            const end_time = `${later.getFullYear()}-${String(later.getMonth() + 1).padStart(2, '0')}-${String(later.getDate()).padStart(2, '0')} ${String(later.getHours()).padStart(2, '0')}:${String(later.getMinutes()).padStart(2, '0')}:00`;
            return { start_time, end_time };
        };

        const getEnovaMeetingLink = (meetingId) => `${window.location.origin}/meetings?room=${meetingId}`;

        const getMeetingShareLink = (meeting) => {
            if (!meeting) return '';
            const isHybrid = meeting.meeting_type === 'hybrid_zoom' || meeting.meeting_type === 'hybrid_google_meet' || meeting.meeting_type === 'hybrid';
            if (isHybrid) {
                return meeting.external_meeting_url || '';
            }
            return getEnovaMeetingLink(meeting.id);
        };

        const copyMeetingLink = async (link, e) => {
            if (e) e.stopPropagation();
            if (!link) return;
            try {
                await navigator.clipboard.writeText(link);
                setAlertPopup({
                    show: true,
                    title: 'Copied',
                    message: 'Meeting link copied to clipboard!'
                });
            } catch (err) {
                console.error('Failed to copy meeting link', err);
            }
        };

        const handleCreateLaterMeetingTypeChange = (e) => {
            const newType = e.target.value;
            setCreateLaterMeetingType(newType);
            // Do not create meeting/link until user clicks Create Meeting
            setCreatedMeetingLink('');
            setCreatedMeetingId('');
        };

        const sendMeetingInviteEmails = useCallback(async ({ meetingId, title, employeeIds = [], guestEmails = [], meetingLink = null }) => {
            return meetingService.sendMeetingInviteEmails({
                meetingId,
                title,
                employeeIds,
                guestEmails,
                meetingLink,
                allEmployeesList,
                currentUser: user
            });
        }, [allEmployeesList, user]);

        const handleSendEmailInvite = async () => {
            if (!inviteEmail.trim()) {
                setAlertPopup({ show: true, title: 'Error', message: 'Please enter at least one recipient email address.' });
                return;
            }

            const emails = parseGuestEmails(inviteEmail);
            if (emails.length === 0) {
                setAlertPopup({ show: true, title: 'Error', message: 'Please enter valid email address(es).' });
                return;
            }
            
            setIsSendingInvite(true);
            try {
                const data = await meetingService.sendEmailInvite({
                    to: emails,
                    subject: `Meeting Invitation: ${createMeetingTitle || 'Collaboration Room'}`,
                    body: `Hello,\n\nYou have been invited to a meeting.\n\nPlease join using the following link:\n${createdMeetingLink}\n\nBest regards,\n${user?.name || 'Your Team'}`
                });

                if (data && data.status === 'success') {
                    const successCount = data.success_count ?? emails.length;
                    const failCount = data.fail_count ?? 0;
                    setAlertPopup({ 
                        show: true, 
                        title: 'Success', 
                        message: failCount > 0 
                            ? `Email invitations sent to ${successCount} address(es). (${failCount} failed)` 
                            : `Email invitation(s) sent successfully to: ${emails.join(', ')}` 
                    });
                    setInviteEmail('');
                } else {
                    setAlertPopup({
                        show: true,
                        title: 'Error',
                        message: data?.message || 'Failed to send the email invitation. Please check system mail server settings.'
                    });
                }
            } catch (err) {
                setAlertPopup({
                    show: true,
                    title: 'Error',
                    message: err.response?.data?.message || 'Failed to send the email invitation. Please check system mail server settings.'
                });
            } finally {
                setIsSendingInvite(false);
            }
        };

        // Google Meet style: Create a meeting for later

        const handleCreateMeetingClick = () => {
            setDropdownOpen(false);
            const randomSuffix = Math.floor(1000 + Math.random() * 9000);
            const titleInput = `Collaboration Room - ${randomSuffix}`;

            setCreatedMeetingId('');
            setCreatedMeetingLink('');
            setMeetingCreationMode('link');
            setCreateLaterMeetingType('');
            setCreateLaterShowEmployees(false);
            setCreateLaterShowCustomers(false);
            setCreateLaterShowExternal(false);
            setEmployeePickerOpen(false);
            setCreateMeetingTitle(titleInput);
            setSelectedEmployees([]);
            setSelectedCustomers([]);
            setEmployeeSearchQuery('');
            setCustomerSearchQuery('');
            setManualEmails('');
            const now = new Date();
            setCreateLaterTime(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
            setShowCreateLaterTargetModal(true);
        };

        const handleStartInstantClick = () => {
            setDropdownOpen(false);
            setMeetingCreationMode('instant');
            setCreateLaterShowEmployees(false);
            setCreateLaterShowCustomers(false);
            setCreateLaterShowExternal(false);
            setEmployeePickerOpen(false);
            setCreateMeetingTitle('');
            setSelectedEmployees([]);
            setSelectedCustomers([]);
            setEmployeeSearchQuery('');
            setCustomerSearchQuery('');
            setManualEmails('');
            setShowCreateLaterTargetModal(true);
        };

        const handleTargetSubmit = async () => {
            const errors = {};
            if (!createLaterShowEmployees && !createLaterShowCustomers) {
                errors.invite = 'Please select Internal Employees or External Guests.';
            }
            if (createLaterShowEmployees && selectedEmployees.length === 0) {
                errors.selective_employees = 'Please select at least one internal employee.';
            }
            if (createLaterShowCustomers && parseGuestEmails(manualEmails).length === 0) {
                errors.manual_emails = 'Please enter at least one valid guest email.';
            }

            if (meetingCreationMode === 'link' && !createLaterMeetingType) {
                errors.meeting_type = 'Please select a meeting type.';
            }

            if (meetingCreationMode === 'later') {
                if (!createLaterDate) {
                    errors.date = 'Please select a date for the meeting.';
                }
                if (!createLaterTime) {
                    errors.time = 'Please select a time for the meeting.';
                }
            }

            if (Object.keys(errors).length > 0) {
                setTargetModalErrors(errors);
                return;
            }
            setTargetModalErrors({});

            setShowCreateLaterTargetModal(false);
            const guestEmailsForInvite = createLaterShowCustomers ? parseGuestEmails(manualEmails) : [];
            setManualEmails('');
            try {
                let titleInput = createMeetingTitle.trim();
                if (!titleInput) {
                    const randomSuffix = Math.floor(1000 + Math.random() * 9000);
                    titleInput = meetingCreationMode === 'instant' 
                        ? `Quick Sync Room - ${Math.floor(100 + Math.random() * 900)}` 
                        : `Collaboration Room - ${randomSuffix}`;
                }
                
                const payload = {
                    title: titleInput,
                    space_id: 1,
                    meeting_type: 'online',
                    check_in_status: scheduleAdmissionControl
                };

                if (meetingCreationMode === 'later') {
                    payload.start_time = `${createLaterDate} ${createLaterTime}:00`;
                    const startDt = new Date(payload.start_time.replace(/-/g, "/"));
                    if (!isNaN(startDt.getTime())) {
                        startDt.setHours(startDt.getHours() + 1);
                        const year = startDt.getFullYear();
                        const month = String(startDt.getMonth() + 1).padStart(2, '0');
                        const day = String(startDt.getDate()).padStart(2, '0');
                        const hours = String(startDt.getHours()).padStart(2, '0');
                        const minutes = String(startDt.getMinutes()).padStart(2, '0');
                        payload.end_time = `${year}-${month}-${day} ${hours}:${minutes}:00`;
                    }
                }
                
                let targetTypes = [];
                let selEmployees = [];
                
                if (createLaterShowEmployees && selectedEmployees.length > 0) {
                    targetTypes.push('selective_employees');
                    selEmployees = [...selEmployees, ...selectedEmployees];
                }
                if (createLaterShowCustomers) {
                    targetTypes.push('customers');
                    if (guestEmailsForInvite.length > 0) {
                        payload.manual_emails = guestEmailsForInvite;
                    }
                }
                
                if (targetTypes.length > 0) {
                    payload.target_type = targetTypes.join(',');
                }
                if (selEmployees.length > 0) {
                    payload.selected_employees = selEmployees;
                }

                if (meetingCreationMode === 'link') {
                    payload.meeting_type = createLaterMeetingType;

                    const { start_time, end_time } = buildLinkModeDateTimes();
                    payload.start_time = start_time;
                    payload.end_time = end_time;
                    payload.auto_schedule = true;

                    const data = await meetingService.scheduleMeeting(payload);
                    if (data && data.status === 'error') {
                        setAlertPopup({
                            show: true,
                            title: data.is_conflict ? 'Time Conflict Detected' : 'Meeting Schedule Error',
                            message: data.message || 'Another meeting is already scheduled at the same time. Please choose a different time slot.'
                        });
                        return;
                    }
                    if (data && data.status === 'success') {
                        const meetingId = data.meeting_id;
                        const externalUrl = data?.meeting_url
                            || data?.data?.external_meeting_url
                            || data?.external_meeting_url;
                        const isHybridType = createLaterMeetingType === 'hybrid_zoom' || createLaterMeetingType === 'hybrid_google_meet' || createLaterMeetingType === 'hybrid';
                        const inviteLink = isHybridType ? (externalUrl || '') : getEnovaMeetingLink(meetingId);

                        setCreatedMeetingId(meetingId);
                        setCreatedMeetingLink(inviteLink);
                        if (data.provider_error) {
                            setLaterModalNote(`Meeting created, but provider link failed.\n\n${data.provider_error}`);
                        } else {
                            setLaterModalNote('');
                        }
                        setShowLaterModal(true);

                        // Send emails asynchronously in the background
                        sendMeetingInviteEmails({
                            meetingId,
                            title: titleInput,
                            employeeIds: selEmployees,
                            guestEmails: payload.manual_emails || [],
                            meetingLink: inviteLink
                        }).then(inviteResult => {
                            if (inviteResult && inviteResult.total > 0 && !data.provider_error) {
                                setLaterModalNote(
                                    inviteResult.successCount > 0
                                        ? `Invite emails sent to ${inviteResult.successCount} recipient(s)${inviteResult.failCount ? ` (${inviteResult.failCount} failed)` : ''}.`
                                        : (inviteResult.message || 'Invite emails could not be sent. Please check mail server settings.')
                                );
                            }
                        }).catch(() => {});
                    }
                } else {
                    const data = await meetingService.scheduleMeeting(payload);
                    if (data && data.status === 'error') {
                        setAlertPopup({
                            show: true,
                            title: data.is_conflict ? 'Time Conflict Detected' : 'Meeting Schedule Error',
                            message: data.message || 'Another meeting is already scheduled at the same time. Please choose a different time slot.'
                        });
                        return;
                    }
                    if (data && data.status === 'success') {
                        const meetingId = data.meeting_id;
                        
                        if (meetingCreationMode === 'instant') {
                            setActiveMeeting({ id: meetingId, name: titleInput });
                            await sendMeetingInviteEmails({
                                meetingId,
                                title: titleInput,
                                employeeIds: selEmployees,
                                guestEmails: payload.manual_emails || []
                            });
                        } else {
                            const externalUrl = data?.meeting_url || data?.data?.external_meeting_url || data?.external_meeting_url || payload.external_meeting_url;
                            const isHybridType = payload.meeting_type === 'hybrid_zoom' || payload.meeting_type === 'hybrid_google_meet' || payload.meeting_type === 'hybrid';
                            const inviteLink = isHybridType ? (externalUrl || '') : getEnovaMeetingLink(meetingId);

                            setCreatedMeetingId(meetingId);
                            setCreatedMeetingLink(inviteLink);
                            setShowLaterModal(true);

                            // Dispatch invite emails asynchronously so popup appears instantly
                            sendMeetingInviteEmails({
                                meetingId,
                                title: titleInput,
                                employeeIds: selEmployees,
                                guestEmails: payload.manual_emails || [],
                                meetingLink: inviteLink
                            }).then(inviteResult => {
                                if (inviteResult && inviteResult.total > 0) {
                                    setLaterModalNote(
                                        inviteResult.successCount > 0
                                            ? `Invite emails sent to ${inviteResult.successCount} recipient(s)${inviteResult.failCount ? ` (${inviteResult.failCount} failed)` : ''}.`
                                            : (inviteResult.message || 'Invite emails could not be sent. Please check mail server settings.')
                                    );
                                }
                            }).catch(() => {});
                        }
                    }
                }

            } catch (error) {
                const status = error.response?.status;
                const serverMsg = error.response?.data?.message || error.response?.data?.error;
                const isConflict = status === 409 || (serverMsg && (serverMsg.toLowerCase().includes('conflict') || serverMsg.toLowerCase().includes('already scheduled') || serverMsg.toLowerCase().includes('booked')));

                const displayTitle = isConflict ? 'Time Conflict Detected' : 'Meeting Schedule Error';
                const displayMsg = serverMsg 
                    || (isConflict 
                        ? 'Another meeting is already scheduled at the same time. Please choose a different time slot.' 
                        : (meetingCreationMode === 'instant' ? 'Failed to start instant meeting.' : 'Failed to schedule meeting room.'));

                setAlertPopup({
                    show: true,
                    title: displayTitle,
                    message: displayMsg
                });
            }

        };
        const handleConfirmLater = () => {
            setShowLaterModal(false);
            setLaterModalNote('');
            fetchMeetings();
        };
        const handleCancelLater = async () => {
            setShowLaterModal(false);
            setLaterModalNote('');
            if (createdMeetingId) {
                try {
                    await meetingService.deleteMeeting(createdMeetingId);
                } catch (error) {
                    console.error('Failed to delete canceled meeting:', error);
                }
            }
        };
        // Google Meet style: Start an instant meeting (Logic moved to handleTargetSubmit, keeping placeholder for reference or future use)

        const handleStartInstant = handleStartInstantClick;
        // Google Meet style: Schedule calendar

        const handleScheduleCalendar = () => {
            setDropdownOpen(false);
            // Initialize form fields with sensible defaults

            setScheduleTitle('');
            setScheduleDate(new Date().toISOString().split('T')[0]);
            setScheduleStartTime('16:00');
            setScheduleEndTime('17:00');
            setScheduleType('');
            setScheduleSpaceId(spaces[0]?.id || 1);
            setScheduleDescription('');
            setScheduleLocation('');
            setScheduleNotification(30);
            setScheduleBusy(true);
            setScheduleVisibility('default');
            setScheduleGuests('');
            setTargetType(['all_employees']);
            setSelectedEmployees([]);
            setSelectedCustomers([]);
            setEmployeeSearchQuery('');
            setCustomerSearchQuery('');
            setManualEmails('');
            setShowScheduleFormModal(true);
        };
        const handleSaveScheduledMeeting = async (e) => {
            e.preventDefault();
            setScheduleFormErrors({});
            const errors = {};
            if (!scheduleTitle.trim()) {
                errors.title = 'Please enter a title.';
            } else if (scheduleTitle.trim().length < 3) {
                errors.title = 'Title must be at least 3 characters in length.';
            }

            if (!scheduleDate) {
                errors.date = 'Please select a date.';
            }

            if (!scheduleStartTime) {
                errors.start_time = 'Please select a start time.';
            }

            if (!scheduleType) {
                errors.schedule_type = 'Please select a meeting type.';
            }

            if (scheduleType === 'physical') {
                if (!scheduleBuilding.trim()) errors.building = 'Building is required for physical meetings.';
                if (!scheduleFloor.trim()) errors.floor = 'Floor is required for physical meetings.';
                if (!scheduleRoomName.trim()) errors.room_name = 'Room name is required for physical meetings.';
                if (!scheduleSeatingCapacity || parseInt(scheduleSeatingCapacity) <= 0) {
                    errors.seating_capacity = 'Seating capacity must be greater than 0.';
                }

            } else if (scheduleType === 'hybrid_zoom' || scheduleType === 'hybrid_google_meet') {
                if (!scheduleAutoSchedule) {
                    const url = scheduleExternalMeetingUrl.trim();
                    if (!url) {
                        errors.external_meeting_url = 'External meeting link is required.';
                    } else {
                        const isValidUrl = /^https?:\/\/[^\s/$.?#].[^\s]*$/i.test(url);
                        if (!isValidUrl) {
                            errors.external_meeting_url = 'Please enter a valid URL (starting with http/https).';
                        } else if (scheduleType === 'hybrid_zoom' && !url.toLowerCase().includes('zoom.us')) {
                            errors.external_meeting_url = 'Zoom link must contain "zoom.us".';
                        } else if (scheduleType === 'hybrid_google_meet' && !url.toLowerCase().includes('google.com')) {
                            errors.external_meeting_url = 'Google Meet link must contain "google.com".';
                        }
                    }
                }
            }

            const startDateTimeTest = `${scheduleDate} ${scheduleStartTime}:00`;
            const startTimestamp = new Date(startDateTimeTest).getTime();
            let endDateTime = '';
            if (scheduleEndTime && scheduleEndTime !== '00:00') {
                const endDateTimeTest = `${scheduleDate} ${scheduleEndTime}:00`;
                const endTimestamp = new Date(endDateTimeTest).getTime();
                if (isNaN(startTimestamp) || isNaN(endTimestamp)) {
                    errors.time = 'Please select a valid date and time.';
                } else if (endTimestamp <= startTimestamp) {
                    errors.time = 'End time must be after the start time.';
                }
                endDateTime = endDateTimeTest;
            } else {
                if (isNaN(startTimestamp)) {
                    errors.time = 'Please select a valid date and time.';
                } else {
                    const startDt = new Date(startDateTimeTest);
                    startDt.setHours(startDt.getHours() + 1);
                    const year = startDt.getFullYear();
                    const month = String(startDt.getMonth() + 1).padStart(2, '0');
                    const day = String(startDt.getDate()).padStart(2, '0');
                    const hours = String(startDt.getHours()).padStart(2, '0');
                    const minutes = String(startDt.getMinutes()).padStart(2, '0');
                    endDateTime = `${year}-${month}-${day} ${hours}:${minutes}:00`;
                }
            }

            if (targetType.includes('selective_employees') && selectedEmployees.length === 0) {
                errors.selective_employees = 'Please select at least one employee.';
            }

            if (targetType.includes('customers') || targetType.includes('manual_emails')) {
                if (!manualEmails.trim()) {
                    errors.manual_emails = 'Please add at least one guest email.';
                } else {
                    const emails = parseGuestEmails(manualEmails);
                    if (emails.length === 0) {
                        errors.manual_emails = 'Please enter valid email address(es).';
                    }
                }
            }

            if (Object.keys(errors).length > 0) {
                setScheduleFormErrors(errors);
                return;
            }

            try {
                const payload = {
                    title: scheduleTitle,
                    space_id: scheduleSpaceId,
                    meeting_type: scheduleType,
                    start_time: startDateTimeTest,
                    end_time: endDateTime,
                    target_type: targetType.join(','),
                    building: scheduleBuilding,
                    floor: scheduleFloor,
                    room_name: scheduleRoomName,
                    seating_capacity: parseInt(scheduleSeatingCapacity) || 0,
                    external_meeting_url: scheduleExternalMeetingUrl,
                    meeting_passcode: scheduleMeetingPasscode,
                    recurring: scheduleRecurring,
                    show_as: scheduleBusy ? 'busy' : 'free',
                    visibility: scheduleVisibility,
                    check_in_status: scheduleAdmissionControl,
                    auto_schedule: scheduleAutoSchedule
                };
                
                payload.selected_employees = [];
                if (targetType.includes('selective_employees')) {
                    payload.selected_employees = [...payload.selected_employees, ...selectedEmployees];
                }

                if (targetType.includes('customers') || targetType.includes('manual_emails')) {
                    payload.manual_emails = parseGuestEmails(manualEmails);
                }

                const data = await meetingService.scheduleMeeting(payload);
                if (data && data.status === 'success') {
                    const meetingId = data.meeting_id;
                    setShowScheduleFormModal(false);
                    setShowCalendarModal(true);
                    fetchMeetings();

                    const externalUrl = data?.meeting_url || data?.data?.external_meeting_url || data?.external_meeting_url || payload.external_meeting_url;
                    const isHybridType = payload.meeting_type === 'hybrid_zoom' || payload.meeting_type === 'hybrid_google_meet' || payload.meeting_type === 'hybrid';
                    const inviteLink = isHybridType ? (externalUrl || '') : getEnovaMeetingLink(meetingId);

                    const inviteResult = await sendMeetingInviteEmails({
                        meetingId,
                        title: scheduleTitle,
                        employeeIds: payload.selected_employees || [],
                        guestEmails: payload.manual_emails || [],
                        meetingLink: inviteLink
                    });
                    
                    if (data.provider_scheduled === false) {
                        setAlertPopup({
                            show: true,
                            title: 'Integration Warning',
                            message: `Meeting scheduled successfully, but the external provider API integration failed.\n\nError: ${data.provider_error || 'Zoom API returned HTTP 400'}\n\nPlease click 'Join Room' from the dashboard to provide a manual meeting URL or switch to the eNova Online Room.`
                        });
                    } else if (inviteResult.total > 0) {
                        setAlertPopup({
                            show: true,
                            title: inviteResult.successCount > 0 ? 'Success' : 'Warning',
                            message: inviteResult.successCount > 0
                                ? `Meeting scheduled. Invite emails sent to ${inviteResult.successCount} recipient(s)${inviteResult.failCount ? ` (${inviteResult.failCount} failed)` : ''}.`
                                : 'Meeting scheduled, but invite emails could not be sent. Please check mail server settings.'
                        });
                    }
                }

            } catch (error) {
                const status = error.response?.status;
                const errorData = error.response?.data;
                if (status === 400 && errorData?.messages) {
                    setScheduleFormErrors(errorData.messages);
                    return;
                }
                const serverMsg = errorData?.message || errorData?.error;
                const isConflict = status === 409 || (serverMsg && (serverMsg.toLowerCase().includes('conflict') || serverMsg.toLowerCase().includes('already scheduled') || serverMsg.toLowerCase().includes('booked')));

                const displayTitle = isConflict ? 'Time Conflict Detected' : 'Error';
                const displayMsg = serverMsg 
                    || (isConflict 
                        ? 'Another meeting is already scheduled at the same time. Please choose a different time slot.' 
                        : 'Failed to schedule meeting room. Check fields.');

                if (status === 409) {
                    setScheduleFormErrors({ room_name: displayMsg });
                }
                setAlertPopup({
                    show: true,
                    title: displayTitle,
                    message: displayMsg
                });
                console.error(error);
            }

        };
        const handleJoinByCode = async (e) => {
            e.preventDefault();
            if (!meetingCodeInput.trim()) return;
            setJoiningMeeting(true);
            try {
                let cleanId = meetingCodeInput.trim();
                // If it's a URL or contains query parameters, try to extract 'room=' or last slash segment

                if (cleanId.includes('room=')) {
                    const urlParams = new URLSearchParams(cleanId.substring(cleanId.indexOf('?')));
                    cleanId = urlParams.get('room');
                } else if (cleanId.includes('/')) {
                    const parts = cleanId.split('/');
                    const lastSegment = parts[parts.length - 1];
                    cleanId = lastSegment.replace(/[^0-9]/g, '');
                } else {
                    cleanId = cleanId.replace(/[^0-9]/g, '');
                }

                if (!cleanId) {
                    setAlertPopup({
                        show: true,
                        title: 'Invalid Input',
                        message: 'Invalid meeting code or URL format.'
                    });
                    setJoiningMeeting(false);
                    return;
                }

                const data = await meetingService.fetchMeeting(cleanId);
                if (data && data.status === 'success') {
                    const meeting = data.data;
                    if (meeting.status === 'completed' || meeting.status === 'cancelled') {
                        setAlertPopup({
                            show: true,
                            title: 'Meeting Ended',
                            message: 'This meeting has already ended.'
                        });
                        setMeetingCodeInput('');
                        return;
                    }
                    setActiveMeeting({ id: meeting.id, name: meeting.title });
                } else {
                    setAlertPopup({
                        show: true,
                        title: 'Not Found',
                        message: 'Meeting room not found.'
                    });
                }
            } catch (error) {
                setAlertPopup({
                    show: true,
                    title: 'Not Found',
                    message: 'Meeting room not found or invalid format.'
                });
                console.error(error);
            } finally {
                setJoiningMeeting(false);
            }
        };
        const copyToClipboard = () => {
            navigator.clipboard.writeText(createdMeetingLink);
            setAlertPopup({
                show: true,
                title: 'Copied',
                message: 'Meeting link copied to clipboard!'
            });
        };

        const isMeetingPast = useCallback((m) => {
            const status = (m.status || '').toLowerCase();
            if (['completed', 'ended', 'cancelled', 'done'].includes(status)) {
                return true;
            }
            if (m.end_time) {
                const normalizedDate = m.end_time.includes('T') ? m.end_time : m.end_time.replace(' ', 'T');
                const endTimeMs = new Date(normalizedDate).getTime();
                if (!isNaN(endTimeMs) && endTimeMs <= Date.now()) {
                    return true;
                }
            }
            return false;
        }, []);

        const upcomingMeetings = useMemo(() => meetings.filter(m => !isMeetingPast(m)), [meetings, isMeetingPast]);
        const previousMeetings = useMemo(() => meetings.filter(m => isMeetingPast(m)), [meetings, isMeetingPast]);
        const nextUpMeeting = useMemo(() => (upcomingMeetings.length > 0 ? upcomingMeetings[0] : null), [upcomingMeetings]);

        const filteredMeetings = useMemo(() => {
            const list = activeTab === 'upcoming' ? upcomingMeetings : previousMeetings;
            const term = searchTerm.trim().toLowerCase();
            if (!term) return list;
            return list.filter(m => 
                (m.title && m.title.toLowerCase().includes(term)) || 
                (m.start_time && m.start_time.includes(term))
            );
        }, [activeTab, upcomingMeetings, previousMeetings, searchTerm]);

        if (!user) {
            if (checkingGuestMeeting) {
                return (
                    <div className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center font-sans">
                        <div className="flex flex-col items-center gap-4">
                            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-main"></div>
                            <p className="text-slate-500 text-sm font-semibold">Checking meeting status...</p>
                        </div>
                    </div>
                );
            }

            if (guestError && !guestMeetingTitle) {
                return (
                    <div className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center font-sans px-4">
                        <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-xl">
                            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h2>
                            <p className="text-slate-550 mb-6">{guestError}</p>
                            <a href="/" className="inline-flex items-center justify-center px-5 py-3 text-sm font-medium text-white bg-main hover:bg-mainHover active:bg-red-800 rounded-lg transition duration-200">
                                Go to Login
                            </a>
                        </div>
                    </div>
                );
            }

            return (
                <div className="min-h-screen w-full bg-gradient-to-br from-white via-red-50/30 to-white text-slate-900 flex items-center justify-center font-sans relative overflow-hidden px-4 py-12">
                    {/* Decorative background grid */}
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:14px_24px] pointer-events-none"></div>
                    {/* Soft red background blobs */}
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute -top-40 -left-40 h-[500px] w-[500px] rounded-full bg-main/5 blur-3xl"></div>
                        <div className="absolute -bottom-40 -right-40 h-[500px] w-[500px] rounded-full bg-red-100/30 blur-3xl"></div>
                        <div className="absolute top-[30%] right-[20%] h-[300px] w-[300px] rounded-full bg-main/5 blur-[120px] animate-pulse"></div>
                    </div>
                    {/* Responsive Layout Container */}
                    <div className="w-full max-w-6xl z-10 flex flex-col lg:flex-row items-center justify-center gap-12">
                        {/* Left Floating Card: Mock Video feeds (Hidden on mobile) */}
                        <div className="hidden lg:flex flex-col gap-6 w-72 shrink-0">
                            {/* Card 1: Host Video Preview */}
                            <div className="bg-white/80 backdrop-blur-md border border-slate-200/60 p-3 rounded-2xl shadow-lg relative overflow-hidden group hover:scale-[1.02] transition-all duration-300">
                                <div className="relative h-44 rounded-xl overflow-hidden bg-slate-100">
                                    <img
                                        src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=300&auto=format&fit=crop"
                                        alt="Marcus Vance"
                                        className="w-full h-full object-cover object-center filter brightness-95"
                                    />
                                    <div className="absolute top-2 left-2 bg-main text-white text-[9px] font-black uppercase px-2 py-0.5 rounded-md tracking-wider flex items-center gap-1">
                                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                                        Host
                                    </div>
                                    <div className="absolute bottom-2 left-2 right-2 flex justify-between items-center">
                                        <span className="text-[10px] text-white bg-slate-900/60 backdrop-blur-md px-2 py-1 rounded font-bold">
                                            Marcus Vance
                                        </span>
                                        <div className="flex gap-1">
                                            <span className="bg-emerald-500 text-white p-1 rounded-md text-[10px] leading-none">
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 0 0 6-6v-1.5m-6 7.5a6 6 0 0 1-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 0 1-3-3V4.5a3 3 0 1 1 6 0v8.25a3 3 0 0 1-3 3Z" />
                                                </svg>
                                            </span>
                                            <span className="bg-emerald-500 text-white p-1 rounded-md text-[10px] leading-none">
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                                                </svg>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Card 2: Participant Face Grid */}
                            <div className="bg-white/80 backdrop-blur-md border border-slate-200/60 p-4 rounded-2xl shadow-lg hover:scale-[1.02] transition-all duration-300">
                                <h4 className="text-xs font-black text-slate-800 mb-3 flex items-center justify-between">
                                    <span>Already Joined</span>
                                    <span className="bg-red-50 text-main text-[10px] px-2 py-0.5 rounded-full font-bold">3 Online</span>
                                </h4>
                                <div className="flex flex-col gap-2.5">
                                    <div className="flex items-center gap-3">
                                        <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=100&auto=format&fit=crop" className="w-8 h-8 rounded-full object-cover" />
                                        <div className="text-left flex-grow">
                                            <p className="text-xs font-bold text-slate-700">David K. (Design)</p>
                                            <p className="text-[9px] text-slate-400 font-medium">Presenter</p>
                                        </div>
                                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=100&auto=format&fit=crop" className="w-8 h-8 rounded-full object-cover" />
                                        <div className="text-left flex-grow">
                                            <p className="text-xs font-bold text-slate-700">Elena Rostova</p>
                                            <p className="text-[9px] text-slate-400 font-medium">Marketing Head</p>
                                        </div>
                                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Center Card: Join Form */}
                        <div className="max-w-md w-full bg-white/95 backdrop-blur-md border border-slate-200/80 rounded-3xl p-8 shadow-[0_20px_50px_rgba(195,0,16,0.06)] relative z-10 hover:border-slate-300/85 transition-all duration-300 shrink-0">
                            <div className="text-center mb-8">
                                <div className="inline-flex items-center justify-center p-3.5 bg-main/10 rounded-2xl mb-4 text-main">
                                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-1">eNova Meet</h2>
                                <p className="text-slate-500 text-xs font-semibold">You've been invited to join</p>
                                <p className="text-main font-black text-lg mt-1 tracking-tight">"{guestMeetingTitle}"</p>
                                {guestMeetingCreator && (
                                    <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 border border-slate-200 rounded-full text-slate-700 text-xs font-bold">
                                        <svg className="w-3.5 h-3.5 text-main" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg>
                                        <span>Created by: {guestMeetingCreator}</span>
                                    </div>
                                )}
                            </div>
                            <form onSubmit={handleGuestJoinSubmit} className="space-y-5">
                                <div>
                                    <label className="block text-slate-650 text-xs font-extrabold uppercase tracking-wider mb-2">
                                        Your Name
                                    </label>
                                    <div className="relative">
                                        <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-slate-400">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                                            </svg>
                                        </span>
                                        <input
                                            type="text"
                                            value={guestName}
                                            onChange={(e) => setGuestName(e.target.value)}
                                            placeholder="Enter your full name"
                                            className="w-full bg-slate-50/50 border border-slate-200 focus:border-main focus:ring-1 focus:ring-main focus:bg-white rounded-xl pl-10 pr-4 py-3.5 text-slate-900 placeholder-slate-400 outline-none transition duration-200 text-sm font-semibold"
                                            required
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-slate-650 text-xs font-extrabold uppercase tracking-wider mb-2">
                                        Your Email Address
                                    </label>
                                    <div className="relative">
                                        <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-slate-400">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                                            </svg>
                                        </span>
                                        <input
                                            type="email"
                                            value={guestEmailInput}
                                            onChange={(e) => setGuestEmailInput(e.target.value)}
                                            placeholder="Enter your email address"
                                            className="w-full bg-slate-50/50 border border-slate-200 focus:border-main focus:ring-1 focus:ring-main focus:bg-white rounded-xl pl-10 pr-4 py-3.5 text-slate-900 placeholder-slate-400 outline-none transition duration-200 text-sm font-semibold"
                                            required />
                                    </div>
                                </div>
                                {guestError && (
                                    <div className="bg-red-50/30 border border-red-200 text-red-650 px-4 py-3 rounded-xl text-xs leading-relaxed font-bold">
                                        {guestError}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={guestLoading}
                                    className="w-full inline-flex items-center justify-center px-4 py-3.5 text-sm font-bold text-white bg-main hover:bg-mainHover active:bg-red-800 disabled:opacity-50 rounded-xl shadow-lg hover:shadow-main/20 transition duration-200"
                                >
                                    {guestLoading ? (
                                        <div className="flex items-center gap-2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                                            <span>Joining...</span>
                                        </div>
                                    ) : (
                                        "Join Meeting"
                                    )}
                                </button>
                            </form>
                            <div className="mt-8 pt-6 border-t border-slate-100 text-center">
                                <span className="text-slate-450 text-xs font-semibold">Have an account?</span>
                                <a href={`/?redirectTo=${encodeURIComponent(`/meetings?room=${roomId}`)}`} className="text-main hover:text-mainHover text-xs font-bold ml-1.5 transition duration-150">
                                    Log In
                                </a>
                            </div>
                        </div>
                        {/* Right Floating Card: AI & Live Transcription Features (Hidden on mobile) */}
                        <div className="hidden lg:flex flex-col gap-6 w-72 shrink-0">
                            <div className="bg-white/80 backdrop-blur-md border border-slate-200/60 p-4 rounded-2xl shadow-lg hover:scale-[1.02] transition-all duration-300">
                                <h4 className="text-xs font-black text-slate-800 mb-3 flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-main animate-pulse"></span>
                                    <span>AI Copilot Status</span>
                                </h4>
                                <div className="space-y-3">
                                    <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-left">
                                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1">Live transcription</p>
                                        <p className="text-xs text-slate-650 italic leading-relaxed">
                                            "Let's review the marketing proposal and update the milestones for Q3..."
                                        </p>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2 text-slate-600 text-xs font-medium">
                                            <svg className="w-4 h-4 text-emerald-500 shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                                            </svg>
                                            <span>Speech Recognition Ready</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-slate-600 text-xs font-medium">
                                            <svg className="w-4 h-4 text-emerald-500 shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                                            </svg>
                                            <span>Live Action Item Capture</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white/80 backdrop-blur-md border border-slate-200/60 p-4 rounded-2xl shadow-lg text-center hover:scale-[1.02] transition-all duration-300">
                                <div className="w-10 h-10 rounded-full bg-main/10 text-main flex items-center justify-center mx-auto mb-2.5">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z" />
                                    </svg>
                                </div>
                                <p className="text-xs font-black text-slate-800">Secure & Encrypted</p>
                                <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">All video streams and minutes are fully encrypted in transit.</p>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (activeMeeting) {
            return (
                <CollabMeetingRoom
                    meetingId={activeMeeting.id}
                    meetingTitle={activeMeeting.name}
                    onLeave={() => {
                        setActiveMeeting(null);
                        setMeetingCodeInput('');
                        window.history.pushState({}, '', window.location.pathname);
                        if (user && String(user.role_id) === '28') {
                            localStorage.removeItem('token');
                            setUser(null);
                        } else {
                            fetchMeetings();
                        }
                    }}
                />
            );
        }

        if (joiningMeeting) {
            return (
                <Layout noSidebar={true}>
                    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-white">
                        <span className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-[#c41230] border-t-transparent mb-4"></span>
                        <h2 className="text-xl font-bold">Connecting to Meeting Room...</h2>
                        <p className="text-xs text-slate-400 mt-2">Preparing audio, video & security verification</p>
                    </div>
                </Layout>
            );
        }

        return (
            <Layout noSidebar={true}>
                <div className="bg-slate-100/70 min-h-[calc(100vh-64px)] font-sans relative">
                    
                    {/* Main Grid Content Area */}
                    <div className="max-w-[1240px] mx-auto px-4 md:px-8 py-6 md:py-8 relative z-10">
                        <h1 className="text-2xl md:text-3xl font-extrabold text-slate-800 tracking-tight mb-6">Meeting</h1>

                        {/* Top Row - Actions */}
                        <div className="mb-6 bg-white rounded-[2rem] p-6 md:p-7 shadow-sm border border-slate-200/60 w-full">
                            <div className="flex items-center justify-center gap-8 sm:gap-12 md:gap-16">
                                <div onClick={handleCreateMeetingClick} className="group cursor-pointer flex flex-col items-center gap-3">
                                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-[1.4rem] bg-[#FF742E] text-white flex items-center justify-center shadow-lg shadow-[#FF742E]/30 group-hover:-translate-y-1 transition-transform duration-300">
                                        <svg className="w-9 h-9 md:w-10 md:h-10" fill="currentColor" viewBox="0 0 24 24"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" /></svg>
                                    </div>
                                    <span className="text-xs md:text-sm font-extrabold text-slate-700 whitespace-nowrap">Meet Now</span>
                                </div>

                                <div onClick={handleScheduleCalendar} className="group cursor-pointer flex flex-col items-center gap-3">
                                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-[1.4rem] bg-[#0E72ED] text-white flex items-center justify-center shadow-lg shadow-[#0E72ED]/30 group-hover:-translate-y-1 transition-transform duration-300">
                                        <svg className="w-9 h-9 md:w-10 md:h-10" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></svg>
                                    </div>
                                    <span className="text-xs md:text-sm font-extrabold text-slate-700 whitespace-nowrap">Schedule Meeting</span>
                                </div>

                                {user?.role_id == 1 && (
                                    <div onClick={() => setShowProviderAdmin(true)} className="group cursor-pointer flex flex-col items-center gap-3">
                                        <div className="w-20 h-20 md:w-24 md:h-24 rounded-[1.4rem] bg-[#0E72ED] text-white flex items-center justify-center shadow-lg shadow-[#0E72ED]/30 group-hover:-translate-y-1 transition-transform duration-300">
                                            <svg className="w-9 h-9 md:w-10 md:h-10" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.281z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                        </div>
                                        <span className="text-xs md:text-sm font-extrabold text-slate-700 whitespace-nowrap">Settings</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Bottom Row - Meetings List */}
                        <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200/60 overflow-hidden flex flex-col min-h-[300px]">
                                    {/* Header Tabs & Search Bar */}
                                <div className="px-5 md:px-8 py-3.5 border-b border-slate-100 flex flex-row items-center justify-between gap-3 flex-wrap">
                                    <div className="flex items-center gap-5">
                                        <button 
                                            onClick={() => { setActiveTab('upcoming'); setDashboardPage(1); }} 
                                            className={`text-sm font-extrabold pb-1 border-b-[3px] transition-all ${activeTab === 'upcoming' ? 'border-[#c41230] text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                                        >
                                            Upcoming
                                        </button>
                                        <button 
                                            onClick={() => { setActiveTab('past'); setDashboardPage(1); }} 
                                            className={`text-sm font-extrabold pb-1 border-b-[3px] transition-all ${activeTab === 'past' ? 'border-[#c41230] text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                                        >
                                            Previous
                                        </button>
                                    </div>

                                    {/* View toggle, Search & Refresh */}
                                    <div className="flex items-center gap-2.5">
                                        <div className="inline-flex items-center rounded-xl border border-slate-200 bg-slate-50/80 p-0.5">
                                            <button
                                                type="button"
                                                onClick={() => setMeetingsViewMode('list')}
                                                title="List view"
                                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-extrabold transition-all ${
                                                    meetingsViewMode === 'list'
                                                        ? 'bg-white text-[#c41230] shadow-sm'
                                                        : 'text-slate-400 hover:text-slate-600'
                                                }`}
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" /></svg>
                                                List
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setMeetingsViewMode('calendar')}
                                                title="Calendar view"
                                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-extrabold transition-all ${
                                                    meetingsViewMode === 'calendar'
                                                        ? 'bg-white text-[#c41230] shadow-sm'
                                                        : 'text-slate-400 hover:text-slate-600'
                                                }`}
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" /></svg>
                                                Calendar
                                            </button>
                                        </div>
                                        <div className="relative">
                                            <input 
                                                type="text" 
                                                placeholder="Search..." 
                                                value={searchTerm}
                                                onChange={(e) => { setSearchTerm(e.target.value); setDashboardPage(1); }}
                                                className="w-36 sm:w-44 h-9 pl-8 pr-3 rounded-xl border border-slate-200 text-xs font-semibold bg-slate-50 outline-none focus:border-slate-400 focus:bg-white transition-all"
                                            />
                                            <svg className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
                                        </div>
                                        <button onClick={fetchMeetings} className="p-2 text-slate-400 hover:text-[#c41230] hover:bg-slate-50 rounded-lg transition-colors" title="Refresh">
                                            <svg className={`h-4 w-4 ${loading ? 'animate-spin text-[#c41230]' : ''}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" /></svg>
                                        </button>
                                    </div>
                                </div>

                                {meetingsViewMode === 'list' ? (
                                /* List Content */
                                <div className="flex-grow flex flex-col">
                                    {loading ? (
                                        <div className="flex-grow flex justify-center items-center py-10">
                                            <span className="inline-block h-8 w-8 animate-spin rounded-full border-[3px] border-[#c41230] border-t-transparent"></span>
                                        </div>
                                    ) : filteredMeetings.length === 0 ? (
                                        <div className="flex-grow flex flex-col items-center justify-center text-center p-6 py-10">
                                            <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center mb-2 border border-slate-100">
                                                <svg className="w-5 h-5 text-slate-300" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                                            </div>
                                            <h4 className="text-xs font-bold text-slate-600">No {activeTab} meetings found</h4>
                                        </div>
                                    ) : (() => {
                                        const MEETINGS_PER_PAGE = 4;
                                        const totalDashboardPages = Math.ceil(filteredMeetings.length / MEETINGS_PER_PAGE) || 1;
                                        const currentDashboardPage = Math.min(dashboardPage, totalDashboardPages);
                                        const paginatedMeetings = filteredMeetings.slice(
                                            (currentDashboardPage - 1) * MEETINGS_PER_PAGE,
                                            currentDashboardPage * MEETINGS_PER_PAGE
                                        );

                                        return (
                                            <div className="flex flex-col h-full">
                                                <div className="space-y-0 divide-y divide-slate-100">
                                                    {paginatedMeetings.map(meeting => (
                                                        <div 
                                                            key={meeting.id}
                                                            className="group hover:bg-slate-50/70 p-5 px-6 md:px-8 transition-colors flex flex-col md:flex-row items-start md:items-center gap-4"
                                                        >
                                                            <div className="space-y-1.5 text-left min-w-0 flex-1">
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <span className="text-[12px] font-medium text-slate-500">
                                                                        {formatMeetingDate(meeting.start_time)} · {formatStartTimeOnly(meeting.start_time)}
                                                                    </span>
                                                                    {(meeting.status === 'completed' || activeTab === 'past') && calculateDuration(meeting) && (
                                                                        <span className="text-[10px] font-semibold text-amber-700">
                                                                            · {calculateDuration(meeting)}
                                                                        </span>
                                                                    )}
                                                                    {(activeTab !== 'upcoming' || (meeting.status && meeting.status !== 'scheduled')) && (
                                                                        <span className={`px-2 py-0.5 rounded-lg text-[9px] font-extrabold uppercase tracking-wider border ${
                                                                            meeting.status === 'in_progress' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' :
                                                                            meeting.status === 'completed' ? 'bg-slate-100 text-slate-500 border-slate-300' :
                                                                            meeting.status === 'cancelled' ? 'bg-red-50 text-red-500 border-red-200' : 'bg-red-50 text-[#c41230] border-red-200'
                                                                        }`}>
                                                                            {meeting.status === 'completed' ? 'COMPLETED' : (meeting.status ? meeting.status.replace('_', ' ') : 'SCHEDULED')}
                                                                        </span>
                                                                    )}
                                                                    {activeTab === 'upcoming' && meeting.id === nextUpMeeting?.id && (
                                                                        <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-[9px] font-extrabold uppercase flex items-center gap-1">
                                                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                                                            Next Up
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                <div className="flex items-center gap-3 flex-wrap min-w-0">
                                                                    <h4 className="text-[15px] font-extrabold text-slate-900 cursor-pointer hover:text-[#c41230] transition-colors shrink-0" onClick={() => setActiveMeeting({ id: meeting.id, name: meeting.title })}>
                                                                        {meeting.title}
                                                                    </h4>
                                                                    {(() => {
                                                                        if (meeting.status === 'completed' || meeting.status === 'cancelled' || activeTab === 'past') return null;
                                                                        const shareLink = getMeetingShareLink(meeting);
                                                                        if (!shareLink) return null;
                                                                        return (
                                                                            <div className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 min-w-0 max-w-full sm:max-w-[380px]">
                                                                                <span className="text-[12px] font-semibold text-slate-700 truncate select-all" title={shareLink}>
                                                                                    {shareLink}
                                                                                </span>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={(e) => copyMeetingLink(shareLink, e)}
                                                                                    title="Copy meeting link"
                                                                                    className="shrink-0 p-1 text-[#c41230] hover:bg-red-50 rounded-lg transition-colors"
                                                                                >
                                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" /></svg>
                                                                                </button>
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </div>

                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wide ${
                                                                        meeting.meeting_type === 'hybrid_zoom'
                                                                            ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                                                            : meeting.meeting_type === 'hybrid_google_meet'
                                                                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                                                : meeting.meeting_type === 'physical'
                                                                                    ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                                                                    : 'bg-red-50 text-[#c41230] border border-red-200'
                                                                    }`}>
                                                                        {getMeetingPlatformLabel(meeting)}
                                                                    </span>
                                                                    {meeting.creator_name && (
                                                                        <span className="text-[10px] font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200 shrink-0">
                                                                            Created by: {meeting.creator_name}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="flex items-center gap-3 shrink-0 self-end md:self-center ml-auto">
                                                                <div className="flex items-center gap-1 border-r border-slate-200 pr-3">
                                                                    {(meeting.created_by == user?.user_id || user?.role_id == 1) && (!meeting.status || meeting.status === 'scheduled') && (new Date(meeting.start_time.replace(/-/g, "/")).getTime() > Date.now()) && (
                                                                        <button onClick={(e) => handleEditMeeting(e, meeting)} title="Edit" className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        onClick={() => handleViewAttendance(meeting)}
                                                                        title={meeting.status === 'completed' || meeting.status === 'cancelled' ? 'Who attended' : 'View invitees'}
                                                                        className="p-1.5 text-slate-400 hover:text-[#c41230] hover:bg-red-50 rounded-lg transition-colors"
                                                                    >
                                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" /></svg>
                                                                    </button>
                                                                    {(meeting.created_by == user?.user_id || user?.role_id == 1) && (
                                                                        <button onClick={(e) => handleDeleteMeeting(e, meeting.id)} title="Delete" className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                                        </button>
                                                                    )}
                                                                </div>

                                                                {!isMeetingPast(meeting) && (
                                                                    <div className="flex items-center gap-2">
                                                                        <button 
                                                                            onClick={() => {
                                                                                const isHybrid = meeting.meeting_type === 'hybrid_zoom' || meeting.meeting_type === 'hybrid_google_meet' || meeting.meeting_type === 'hybrid';
                                                                                if (isHybrid) {
                                                                                    const isHost = meeting.created_by == user?.user_id || user?.role_id == 1;
                                                                                    const launchUrl = (isHost && meeting.start_url) ? meeting.start_url : meeting.external_meeting_url;
                                                                                    if (launchUrl) {
                                                                                        window.open(launchUrl, '_blank');
                                                                                    } else {
                                                                                        handleEditMeeting({ stopPropagation: () => {} }, meeting);
                                                                                    }
                                                                                } else {
                                                                                    setActiveMeeting({ id: meeting.id, name: meeting.title });
                                                                                }
                                                                            }}
                                                                            className="px-5 py-2.5 bg-[#c41230] hover:bg-[#a00f27] text-white text-xs font-black rounded-xl shadow-md transition-all flex items-center gap-1.5"
                                                                        >
                                                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" /></svg>
                                                                            {meeting.created_by == user?.user_id || user?.role_id == 1 ? 'Start Room' : 'Join Room'}
                                                                        </button>
                                                                        {(meeting.meeting_type === 'hybrid_google_meet' || (meeting.meeting_type === 'hybrid' && meeting.external_meeting_url?.toLowerCase().includes('meet.google.com'))) && (meeting.created_by == user?.user_id || user?.role_id == 1 || user?.role_id == 2) && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => handleEndMeeting(meeting.id, e)}
                                                                                className="px-4 py-2.5 bg-[#c41230] hover:bg-[#a00f27] text-white text-xs font-black rounded-xl shadow-md transition-all flex items-center gap-1.5"
                                                                                title="End Google Meet Meeting"
                                                                            >
                                                                                <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 7.5A2.25 2.25 0 017.5 5.25h9a2.25 2.25 0 012.25 2.25v9a2.25 2.25 0 01-2.25 2.25h-9a2.25 2.25 0 01-2.25-2.25v-9z" />
                                                                                </svg>
                                                                                End
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                {totalDashboardPages > 1 && (
                                                    <div className="p-4 px-6 md:px-8 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4 mt-auto bg-slate-50/50">
                                                        <span className="text-[11px] font-bold text-slate-500">
                                                            Showing {(currentDashboardPage - 1) * MEETINGS_PER_PAGE + 1} - {Math.min(currentDashboardPage * MEETINGS_PER_PAGE, filteredMeetings.length)} of {filteredMeetings.length}
                                                        </span>
                                                        <div className="flex items-center gap-1.5">
                                                            <button
                                                                disabled={currentDashboardPage === 1}
                                                                onClick={() => setDashboardPage(prev => Math.max(prev - 1, 1))}
                                                                className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-[11px] font-extrabold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                                                            >
                                                                Previous
                                                            </button>
                                                            <div className="flex items-center gap-1 px-1">
                                                                {Array.from({ length: totalDashboardPages }, (_, i) => i + 1).map(pageNum => (
                                                                    <button
                                                                        key={pageNum}
                                                                        onClick={() => setDashboardPage(pageNum)}
                                                                        className={`w-7 h-7 rounded-lg text-[11px] font-black transition-all ${
                                                                            currentDashboardPage === pageNum
                                                                                ? 'bg-[#c41230] text-white shadow-sm'
                                                                                : 'bg-transparent text-slate-600 hover:bg-slate-200'
                                                                        }`}
                                                                    >
                                                                        {pageNum}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                            <button
                                                                disabled={currentDashboardPage === totalDashboardPages}
                                                                onClick={() => setDashboardPage(prev => Math.min(prev + 1, totalDashboardPages))}
                                                                className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-[11px] font-extrabold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                                                            >
                                                                Next
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                                ) : (
                                /* Calendar Content (Zoom-style) */
                                <div className="flex-grow flex flex-col min-h-0">
                                    {loading ? (
                                        <div className="flex-grow flex justify-center items-center py-20">
                                            <span className="inline-block h-8 w-8 animate-spin rounded-full border-[3px] border-[#c41230] border-t-transparent"></span>
                                        </div>
                                    ) : (() => {
                                        const monthLabel = calendarCursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                                        const monthCells = buildMonthGrid(calendarCursor);
                                        const todayKey = toDateKey(new Date());
                                        const selectedKey = toDateKey(selectedCalendarDay);
                                        const meetingsByDay = filteredMeetings.reduce((acc, meeting) => {
                                            const key = toDateKey(meeting.start_time);
                                            if (!key) return acc;
                                            if (!acc[key]) acc[key] = [];
                                            acc[key].push(meeting);
                                            return acc;
                                        }, {});

                                        const shiftMonth = (delta) => {
                                            setCalendarCursor((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
                                        };

                                        const goToday = () => {
                                            const now = new Date();
                                            setCalendarCursor(new Date(now.getFullYear(), now.getMonth(), 1));
                                            setSelectedCalendarDay(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
                                        };

                                        return (
                                            <div className="flex flex-col flex-1 min-h-0 p-4 md:p-6">
                                                <div className="px-1 pb-3 flex items-center justify-between gap-3 flex-wrap shrink-0 relative z-20">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <div className="relative">
                                                            <select
                                                                value={calendarCursor.getMonth()}
                                                                onChange={(e) => {
                                                                    const newMonth = parseInt(e.target.value, 10);
                                                                    setCalendarCursor(new Date(calendarCursor.getFullYear(), newMonth, 1));
                                                                }}
                                                                className="appearance-none bg-white border border-slate-200 rounded-xl px-3.5 py-1.5 pr-7 text-xs font-bold text-blue-600 outline-none focus:border-blue-400 cursor-pointer shadow-2xs hover:bg-slate-50 transition-colors"
                                                            >
                                                                {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, idx) => (
                                                                    <option key={m} value={idx}>{m}</option>
                                                                ))}
                                                            </select>
                                                            <svg className="w-3 h-3 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                                                        </div>

                                                        <div className="relative">
                                                            <select
                                                                value={calendarCursor.getFullYear()}
                                                                onChange={(e) => {
                                                                    const newYear = parseInt(e.target.value, 10);
                                                                    setCalendarCursor(new Date(newYear, calendarCursor.getMonth(), 1));
                                                                }}
                                                                className="appearance-none bg-white border border-slate-200 rounded-xl px-3.5 py-1.5 pr-7 text-xs font-bold text-blue-600 outline-none focus:border-blue-400 cursor-pointer shadow-2xs hover:bg-slate-50 transition-colors"
                                                            >
                                                                {Array.from({ length: 16 }, (_, i) => 2020 + i).map((y) => (
                                                                    <option key={y} value={y}>{y}</option>
                                                                ))}
                                                            </select>
                                                            <svg className="w-3 h-3 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                                                        </div>

                                                        <button
                                                            type="button"
                                                            onClick={() => shiftMonth(-1)}
                                                            className="w-8 h-8 rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 flex items-center justify-center transition-all shadow-2xs ml-1"
                                                            aria-label="Previous month"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
                                                        </button>

                                                        <button
                                                            type="button"
                                                            onClick={() => shiftMonth(1)}
                                                            className="w-8 h-8 rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 flex items-center justify-center transition-all shadow-2xs"
                                                            aria-label="Next month"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="flex flex-col flex-1 min-h-0 rounded-2xl border border-slate-200 overflow-hidden bg-white shadow-sm">
                                                    <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50/50 shrink-0">
                                                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                                                            <div key={d} className="px-2 py-2 text-[10px] font-extrabold text-slate-400 uppercase tracking-wider text-center border-r border-slate-100 last:border-r-0">
                                                                {d}
                                                            </div>
                                                        ))}
                                                    </div>

                                                    <div className="grid grid-cols-7 flex-1 min-h-0" style={{ gridAutoRows: 'minmax(72px, 1fr)' }}>
                                                        {monthCells.map((cell) => {
                                                            const key = toDateKey(cell.date);
                                                            const dayMeetings = meetingsByDay[key] || [];
                                                            const isSelected = key === selectedKey;
                                                            const isToday = key === todayKey;
                                                            const openDayMeetings = () => {
                                                                setSelectedCalendarDay(cell.date);
                                                                if (!cell.inCurrentMonth) {
                                                                    setCalendarCursor(new Date(cell.date.getFullYear(), cell.date.getMonth(), 1));
                                                                }
                                                                if (dayMeetings.length > 0) {
                                                                    setCalendarDayModal({
                                                                        show: true,
                                                                        date: cell.date,
                                                                        meetings: dayMeetings
                                                                    });
                                                                }
                                                            };
                                                            return (
                                                                <div
                                                                    key={`main-${key}-${cell.inCurrentMonth ? 'c' : 'o'}`}
                                                                    onClick={openDayMeetings}
                                                                    className={`h-full min-h-[72px] border-r border-b border-slate-100 p-1.5 cursor-pointer transition-colors overflow-hidden flex flex-col last:border-r-0 ${
                                                                        isSelected ? 'bg-red-50' : 'hover:bg-slate-50/80'
                                                                    } ${!cell.inCurrentMonth ? 'bg-slate-50/40' : 'bg-white'}`}
                                                                >
                                                                    <div className="flex justify-end items-start shrink-0 mb-1 h-6">
                                                                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-extrabold leading-none ${
                                                                            isToday || isSelected
                                                                                ? 'bg-[#c41230] text-white'
                                                                                : cell.inCurrentMonth
                                                                                    ? 'text-slate-700'
                                                                                    : 'text-slate-300'
                                                                        }`}>
                                                                            {cell.date.getDate()}
                                                                        </span>
                                                                    </div>
                                                                    <div className="space-y-0.5 flex-1 min-h-0 overflow-hidden">
                                                                        {dayMeetings.slice(0, 2).map((m) => (
                                                                            <div
                                                                                key={m.id}
                                                                                className="w-full text-left px-1.5 py-0.5 rounded bg-[#c41230] pointer-events-none"
                                                                                title={`${formatStartTimeOnly(m.start_time)} ${m.title}`}
                                                                            >
                                                                                <span className="flex items-center gap-1 min-w-0">
                                                                                    <span className="text-[9px] font-bold text-white shrink-0">{formatStartTimeOnly(m.start_time)}</span>
                                                                                    <span className="text-[9px] font-semibold text-white/95 truncate">{m.title}</span>
                                                                                </span>
                                                                            </div>
                                                                        ))}
                                                                        {dayMeetings.length > 2 && (
                                                                            <p className="text-[9px] font-extrabold text-[#c41230] px-1 pt-0.5">
                                                                                +{dayMeetings.length - 2} more
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                                )}
                            </div>
                    </div>
                </div>

                {/* Calendar day meetings popup */}
                {calendarDayModal.show && (
                    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fadeIn">
                        <div className="bg-white rounded-2xl p-5 max-w-md w-full shadow-2xl border border-slate-200 text-left flex flex-col max-h-[80vh]">
                            <div className="flex justify-between items-center border-b border-slate-100 pb-3 mb-3 shrink-0">
                                <div>
                                    <h3 className="text-sm font-extrabold text-slate-900">
                                        {calendarDayModal.date
                                            ? calendarDayModal.date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })
                                            : 'Meetings'}
                                    </h3>
                                    <p className="text-[11px] font-bold text-slate-400 mt-0.5">
                                        {calendarDayModal.meetings.length} meeting{calendarDayModal.meetings.length === 1 ? '' : 's'}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setCalendarDayModal({ show: false, date: null, meetings: [] })}
                                    className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-100 rounded-full transition-colors"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                            <div className="overflow-y-auto custom-scrollbar flex-grow space-y-2 pr-1">
                                {calendarDayModal.meetings.map((m) => (
                                    <div key={m.id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-100 bg-slate-50/70">
                                        <div className="min-w-0 flex-1">
                                            <p className="text-[10px] font-extrabold text-[#c41230]">{formatStartTimeOnly(m.start_time)}</p>
                                            <p className="text-xs font-extrabold text-slate-800 truncate">{m.title}</p>
                                            <p className="text-[10px] font-bold text-slate-400 mt-0.5">{getMeetingPlatformLabel(m)}</p>
                                            {(() => {
                                                const shareLink = getMeetingShareLink(m);
                                                if (!shareLink) return null;
                                                return (
                                                    <div className="flex items-center gap-1 mt-1 max-w-full">
                                                        <span className="text-[9px] font-semibold text-slate-500 truncate" title={shareLink}>{shareLink}</span>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => copyMeetingLink(shareLink, e)}
                                                            title="Copy meeting link"
                                                            className="shrink-0 p-0.5 text-[#c41230] hover:bg-red-50 rounded"
                                                        >
                                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" /></svg>
                                                        </button>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                        {m.status !== 'completed' && m.status !== 'cancelled' ? (
                                            <div className="flex items-center gap-1 shrink-0">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setCalendarDayModal({ show: false, date: null, meetings: [] });
                                                        const isHybrid = m.meeting_type === 'hybrid_zoom' || m.meeting_type === 'hybrid_google_meet' || m.meeting_type === 'hybrid';
                                                        if (isHybrid) {
                                                            if (m.external_meeting_url) {
                                                                window.open(m.external_meeting_url, '_blank');
                                                            } else {
                                                                handleEditMeeting({ stopPropagation: () => {} }, m);
                                                            }
                                                        } else {
                                                            setActiveMeeting({ id: m.id, name: m.title });
                                                        }
                                                    }}
                                                    className="shrink-0 px-3 py-1.5 rounded-lg bg-[#c41230] hover:bg-[#a00f27] text-white text-[10px] font-extrabold"
                                                >
                                                    {m.created_by == user?.user_id || user?.role_id == 1 ? 'Start' : 'Join'}
                                                </button>
                                                {(m.meeting_type === 'hybrid_google_meet' || (m.meeting_type === 'hybrid' && m.external_meeting_url?.toLowerCase().includes('meet.google.com'))) && (m.created_by == user?.user_id || user?.role_id == 1 || user?.role_id == 2) && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            setCalendarDayModal({ show: false, date: null, meetings: [] });
                                                            handleEndMeeting(m.id, e);
                                                        }}
                                                        className="shrink-0 px-2.5 py-1.5 rounded-lg bg-[#c41230] hover:bg-[#a00f27] text-white text-[10px] font-extrabold"
                                                        title="End Google Meet Meeting"
                                                    >
                                                        End
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setCalendarDayModal({ show: false, date: null, meetings: [] });
                                                    handleViewAttendance(m);
                                                }}
                                                className="shrink-0 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-[10px] font-extrabold hover:bg-slate-50"
                                            >
                                                View
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <button
                                type="button"
                                onClick={() => setCalendarDayModal({ show: false, date: null, meetings: [] })}
                                className="mt-4 shrink-0 w-full py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-extrabold border border-slate-200"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}

                {/* Modal for Copyable Links */}

                {showLaterModal && (
                    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl border border-slate-200 text-left space-y-4">
                            <div className="flex justify-between items-center">
                                <h3 className="text-base font-extrabold text-slate-900">Here's the link to your meeting</h3>
                                <button onClick={handleCancelLater} className="text-slate-400 hover:text-slate-600">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                            <p className="text-xs text-slate-500 leading-relaxed">
                                Copy this meeting link and send it to people you want to meet with. Make sure you save it so you can use it later.
                            </p>
                            {laterModalNote ? (
                                <p className={`text-xs rounded-xl px-3 py-2 leading-relaxed border ${
                                    /fail|could not|warning/i.test(laterModalNote)
                                        ? 'text-amber-800 bg-amber-50 border-amber-100'
                                        : 'text-emerald-700 bg-emerald-50 border-emerald-100'
                                }`}>
                                    {laterModalNote}
                                </p>
                            ) : null}
                            <div className="bg-slate-100 rounded-xl p-3 flex justify-between items-center border border-slate-200 gap-2">
                                <span className="text-xs font-medium text-slate-600 select-all truncate">{createdMeetingLink}</span>
                                <button onClick={copyToClipboard} className="text-main flex-shrink-0 hover:text-mainHover p-1 hover:bg-slate-200 rounded transition-colors">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" /></svg>
                                </button>
                            </div>

                            <button
                                onClick={handleConfirmLater}
                                className="bg-main hover:bg-mainHover text-white font-bold w-full py-2.5 rounded-xl transition-all text-xs text-center shadow-md shadow-main/15"
                            >
                                Got it
                            </button>
                        </div>
                    </div>
                )}

                {/* Modal for Target Selection before Creating Later Meeting */}
                {showCreateLaterTargetModal && (
                    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fadeIn">
                        <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl border border-slate-200 text-left flex flex-col max-h-[90vh] overflow-hidden">
                            <div className="flex justify-between items-center border-b border-slate-100 pb-3 mb-4 shrink-0">
                                <h3 className="text-sm font-extrabold text-slate-950 font-sans">
                                    {meetingCreationMode === 'link'
                                        ? 'Instant Meeting'
                                        : meetingCreationMode === 'later'
                                            ? 'Schedule a Meeting'
                                            : 'Who is this meeting for?'}
                                </h3>
                                <button onClick={() => setShowCreateLaterTargetModal(false)} className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-100 rounded-full transition-colors">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                            <div className="overflow-y-auto custom-scrollbar flex-grow space-y-4 text-xs pr-1">
                                <div className="mb-2">
                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Meeting Title (Optional)</label>
                                    <input
                                        type="text"
                                        placeholder={meetingCreationMode === 'instant' ? "Quick Sync Room..." : "Collaboration Room..."}
                                        value={createMeetingTitle}
                                        onChange={(e) => setCreateMeetingTitle(e.target.value)}
                                        className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-700 focus:border-blue-500 outline-none shadow-sm"
                                    />
                                </div>
                                {meetingCreationMode === 'later' && (
                                    <div className="flex gap-4 mb-4">
                                        <div className="flex-1">
                                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                                                Date <span className="text-red-500 ml-0.5">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                value={createLaterDate}
                                                onChange={(e) => {
                                                    setCreateLaterDate(e.target.value);
                                                    if (targetModalErrors.date) setTargetModalErrors(prev => ({ ...prev, date: null }));
                                                }}
                                                className={`w-full bg-white border ${targetModalErrors.date ? 'border-red-500' : 'border-slate-200'} rounded-lg px-2.5 py-1.5 text-xs text-slate-700 focus:border-blue-500 outline-none shadow-sm`}
                                            />
                                            {targetModalErrors.date && (
                                                <p className="text-[10px] text-red-500 font-bold mt-1">{targetModalErrors.date}</p>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                                                Time <span className="text-red-500 ml-0.5">*</span>
                                            </label>
                                            <input
                                                type="time"
                                                value={createLaterTime}
                                                onChange={(e) => {
                                                    setCreateLaterTime(e.target.value);
                                                    if (targetModalErrors.time) setTargetModalErrors(prev => ({ ...prev, time: null }));
                                                }}
                                                className={`w-full bg-white border ${targetModalErrors.time ? 'border-red-500' : 'border-slate-200'} rounded-lg px-2.5 py-1.5 text-xs text-slate-700 focus:border-blue-500 outline-none shadow-sm`}
                                            />
                                            {targetModalErrors.time && (
                                                <p className="text-[10px] text-red-500 font-bold mt-1">{targetModalErrors.time}</p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {meetingCreationMode === 'link' && (
                                    <div className="mb-2">
                                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                                            Meeting Type <span className="text-red-500 ml-0.5">*</span>
                                        </label>
                                        <MeetingTypeSelect
                                            value={createLaterMeetingType}
                                            onChange={(e) => {
                                                handleCreateLaterMeetingTypeChange(e);
                                                if (targetModalErrors.meeting_type) setTargetModalErrors(prev => ({ ...prev, meeting_type: null }));
                                            }}
                                            className={`w-full bg-white border ${targetModalErrors.meeting_type ? 'border-red-500' : 'border-slate-200'} rounded-lg px-3 py-2.5 text-xs text-slate-700 outline-none focus:border-blue-500 cursor-pointer shadow-sm hover:border-slate-300 transition-colors ${creatingProviderLink ? 'opacity-60 pointer-events-none' : ''}`}
                                        />
                                        {targetModalErrors.meeting_type && (
                                            <p className="text-[10px] text-red-500 font-bold mt-1">{targetModalErrors.meeting_type}</p>
                                        )}
                                    </div>
                                )}
                                
                                {/* Admission Control Selection for Zoom & Google Meet */}
                                <div className="space-y-1.5 mb-3">
                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Participant Admission Control</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <label className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${scheduleAdmissionControl === 'admit' ? 'border-main bg-red-50/60 text-slate-900 font-extrabold shadow-xs' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}>
                                            <input
                                                type="radio"
                                                name="modalAdmissionControlTarget"
                                                value="admit"
                                                checked={scheduleAdmissionControl === 'admit'}
                                                onChange={() => setScheduleAdmissionControl('admit')}
                                                className="text-main focus:ring-0 cursor-pointer"
                                            />
                                            <div className="text-[11px]">
                                                <span className="block font-bold">Host Admit Required</span>
                                                <span className="text-[9px] text-slate-400 font-normal">Waiting room / admit</span>
                                            </div>
                                        </label>
                                        <label className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${scheduleAdmissionControl === 'direct' ? 'border-main bg-red-50/60 text-slate-900 font-extrabold shadow-xs' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}>
                                            <input
                                                type="radio"
                                                name="modalAdmissionControlTarget"
                                                value="direct"
                                                checked={scheduleAdmissionControl === 'direct'}
                                                onChange={() => setScheduleAdmissionControl('direct')}
                                                className="text-main focus:ring-0 cursor-pointer"
                                            />
                                            <div className="text-[11px]">
                                                <span className="block font-bold">Direct Join</span>
                                                <span className="text-[9px] text-slate-400 font-normal">No host admit needed</span>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                        Invite Options <span className="text-red-500 ml-0.5">*</span>
                                    </label>
                                    {targetModalErrors.invite && (
                                        <p className="text-[10px] text-red-500 font-bold mb-1">{targetModalErrors.invite}</p>
                                    )}

                                    {/* Option 1: Selective Employees */}
                                    <label className={`flex items-start gap-2.5 cursor-pointer rounded-lg border px-3 py-2.5 transition-colors ${createLaterShowEmployees ? 'border-blue-400 bg-blue-50/60' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                                        <input
                                            type="checkbox"
                                            checked={createLaterShowEmployees}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                setCreateLaterShowEmployees(checked);
                                                if (targetModalErrors.invite || targetModalErrors.selective_employees) {
                                                    setTargetModalErrors(prev => ({ ...prev, invite: null, selective_employees: null }));
                                                }
                                                if (checked) {
                                                    setEmployeePickerOpen(true);
                                                } else {
                                                    setEmployeePickerOpen(false);
                                                }
                                            }}
                                            className="mt-0.5 w-4 h-4 rounded border-2 border-slate-300 text-blue-600 focus:ring-0 cursor-pointer shrink-0 accent-blue-600"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="block font-semibold text-slate-700 text-xs">Internal Employees</span>
                                                {createLaterShowEmployees && selectedEmployees.length > 0 && !employeePickerOpen && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setEmployeePickerOpen(true);
                                                        }}
                                                        className="text-[10px] font-bold text-blue-600 hover:text-blue-700 whitespace-nowrap"
                                                    >
                                                        Change ({selectedEmployees.length})
                                                    </button>
                                                )}
                                            </div>
                                            <p className="text-[10px] text-slate-400 mt-0.5 leading-snug">
                                                {createLaterShowEmployees && selectedEmployees.length > 0 && !employeePickerOpen
                                                    ? `${selectedEmployees.length} internal employee${selectedEmployees.length === 1 ? '' : 's'} selected`
                                                    : 'Select internal employees to invite — they will receive an email with the meeting link.'}
                                            </p>
                                        </div>
                                    </label>
                                    {targetModalErrors.selective_employees && (
                                        <p className="text-[10px] text-red-500 font-bold ml-1">{targetModalErrors.selective_employees}</p>
                                    )}

                                    {/* Employee Selection Dropdown / Checkbox List */}
                                    {createLaterShowEmployees && employeePickerOpen && (
                                        <div className="animate-fadeIn">
                                            <div className={`bg-white rounded-lg border ${scheduleFormErrors.selective_employees ? 'border-red-500' : 'border-slate-200'} overflow-hidden flex flex-col`}>
                                                <div className="sticky top-0 bg-white z-10 border-b border-slate-100 p-2 flex items-center gap-2">
                                                    <input
                                                        type="text"
                                                        placeholder="Search employees..."
                                                        value={employeeSearchQuery}
                                                        onChange={(e) => setEmployeeSearchQuery(e.target.value)}
                                                        className="flex-1 min-w-0 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-700 focus:border-blue-500 focus:bg-white outline-none placeholder-slate-400"
                                                    />
                                                    {allEmployeesList.length > 0 && (
                                                        <label className="flex items-center gap-1.5 cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5 shrink-0 whitespace-nowrap">
                                                            <span className="text-slate-700 text-[10px] font-bold uppercase tracking-wider">Select All</span>
                                                            <input
                                                                type="checkbox"
                                                                checked={(() => {
                                                                    const filtered = allEmployeesList.filter(emp =>
                                                                        emp.name.toLowerCase().includes(employeeSearchQuery.toLowerCase()) ||
                                                                        (emp.email && emp.email.toLowerCase().includes(employeeSearchQuery.toLowerCase()))
                                                                    );
                                                                    return filtered.length > 0 && filtered.every(emp => selectedEmployees.includes(emp.user_id));
                                                                })()}
                                                                onChange={(e) => {
                                                                    const filteredEmployeesForSelection = allEmployeesList.filter(emp =>
                                                                        emp.name.toLowerCase().includes(employeeSearchQuery.toLowerCase()) ||
                                                                        (emp.email && emp.email.toLowerCase().includes(employeeSearchQuery.toLowerCase()))
                                                                    );
                                                                    if (e.target.checked) {
                                                                        const newSelected = [...selectedEmployees];
                                                                        filteredEmployeesForSelection.forEach(emp => {
                                                                            if (!newSelected.includes(emp.user_id)) newSelected.push(emp.user_id);
                                                                        });
                                                                        setSelectedEmployees(newSelected);
                                                                    } else {
                                                                        const filteredIds = filteredEmployeesForSelection.map(emp => emp.user_id);
                                                                        setSelectedEmployees(prev => prev.filter(id => !filteredIds.includes(id)));
                                                                    }
                                                                }}
                                                                className="rounded-sm border-slate-350 bg-white text-blue-500 focus:ring-0 w-3.5 h-3.5 cursor-pointer"
                                                            />
                                                        </label>
                                                    )}
                                                </div>
                                                <div className="max-h-[165px] overflow-y-auto custom-scrollbar px-2 pb-2 pt-1 space-y-0.5">
                                                    {(() => {
                                                        const filteredEmployeesForSelection = allEmployeesList
                                                            .filter(emp =>
                                                                emp.name.toLowerCase().includes(employeeSearchQuery.toLowerCase()) ||
                                                                (emp.email && emp.email.toLowerCase().includes(employeeSearchQuery.toLowerCase()))
                                                            )
                                                            .sort((a, b) => {
                                                                const aSelected = selectedEmployees.includes(a.user_id) ? 0 : 1;
                                                                const bSelected = selectedEmployees.includes(b.user_id) ? 0 : 1;
                                                                if (aSelected !== bSelected) return aSelected - bSelected;
                                                                return a.name.localeCompare(b.name);
                                                            });

                                                        if (allEmployeesList.length === 0) {
                                                            return <span className="text-[10px] text-slate-500">Loading employees...</span>;
                                                        }
                                                        if (filteredEmployeesForSelection.length === 0) {
                                                            return <span className="text-[10px] text-slate-400 block p-1">No employees found</span>;
                                                        }
                                                        return filteredEmployeesForSelection.map(emp => {
                                                            const isSelected = selectedEmployees.includes(emp.user_id);
                                                            return (
                                                                <label
                                                                    key={emp.user_id}
                                                                    className={`flex items-center justify-between cursor-pointer py-1.5 px-2 rounded-md transition-colors ${isSelected ? 'bg-blue-50 text-blue-800' : 'hover:bg-slate-50 text-slate-700'}`}
                                                                >
                                                                    <span className={`text-[11px] ${isSelected ? 'font-semibold' : ''}`}>{emp.name}</span>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isSelected}
                                                                        onChange={(e) => {
                                                                            if (e.target.checked) {
                                                                                setSelectedEmployees(prev => [...prev, emp.user_id]);
                                                                            } else {
                                                                                setSelectedEmployees(prev => prev.filter(id => id !== emp.user_id));
                                                                            }
                                                                        }}
                                                                        className="rounded-sm border-slate-350 bg-white text-blue-500 focus:ring-0 w-3.5 h-3.5 cursor-pointer"
                                                                    />
                                                                </label>
                                                            );
                                                        });
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Option 2: External Guests */}
                                    <label className={`flex items-start gap-2.5 cursor-pointer rounded-lg border px-3 py-2.5 transition-colors ${createLaterShowCustomers ? 'border-blue-400 bg-blue-50/60' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                                        <input
                                            type="checkbox"
                                            checked={createLaterShowCustomers}
                                            onChange={(e) => {
                                                setCreateLaterShowCustomers(e.target.checked);
                                            }}
                                            className="mt-0.5 w-4 h-4 rounded border-2 border-slate-300 text-blue-600 focus:ring-0 cursor-pointer shrink-0 accent-blue-600"
                                        />
                                        <div className="min-w-0">
                                            <span className="block font-semibold text-slate-700 text-xs">External Guests (Email)</span>
                                            <p className="text-[10px] text-slate-400 mt-0.5 leading-snug">Send an email invitation with the meeting link.</p>
                                        </div>
                                    </label>

                                    {/* Customer Selection Dropdown / Checkbox List */}
                                    {createLaterShowCustomers && (
                                        <div className="pl-1 animate-fadeIn">
                                            <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">Guest Emails</label>
                                            <textarea
                                                placeholder={"john@example.com\nalice@example.com"}
                                                value={manualEmails}
                                                onChange={(e) => setManualEmails(e.target.value)}
                                                className="bg-white border border-slate-200 rounded-lg p-2 outline-none text-slate-700 text-[11px] w-full h-16 focus:border-blue-500 placeholder-slate-400 resize-none shadow-sm"
                                            />
                                            <p className="text-[9px] text-slate-400 mt-1">Separate emails with comma or new line</p>
                                        </div>
                                    )}
                                </div>

                            </div>
                            <div className="flex gap-3 pt-4 border-t border-slate-100 mt-2 shrink-0">
                                <button
                                    onClick={() => {
                                        setShowCreateLaterTargetModal(false);
                                        setCreatedMeetingId('');
                                        setCreatedMeetingLink('');
                                    }}
                                    className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold flex-1 py-2.5 rounded-xl transition-all text-xs text-center border border-slate-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleTargetSubmit}
                                    className="bg-main hover:bg-mainHover text-white font-bold flex-1 py-2.5 rounded-xl transition-all text-xs text-center shadow-md shadow-main/15"
                                >
                                    {meetingCreationMode === 'instant' ? 'Start Meeting' : 'Create Meeting'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal for Google Calendar style Scheduling Event Dialog */}

                {showScheduleFormModal && (
                    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                        <div className="bg-white text-slate-800 rounded-2xl w-full max-w-xl shadow-2xl border border-slate-200 text-left flex flex-col max-h-[90vh] overflow-hidden">
                            {/* Header */}
                            <div className="flex justify-between items-center px-5 py-3.5 border-b border-slate-200 bg-slate-50/50">
                                <div className="flex items-center gap-3">
                                    <span className="font-extrabold text-sm text-slate-700">Schedule Meeting</span>
                                </div>
                            </div>
                            {/* Form Body */}

                            <form onSubmit={handleSaveScheduledMeeting} className="flex flex-col flex-grow overflow-hidden relative">
                                <div className="p-5 overflow-y-auto flex-grow space-y-4 custom-scrollbar">
                                    {/* Event Details */}
                                    {/* Title Field */}

                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 font-sans">
                                            Meeting Title <span className="text-red-500 ml-0.5">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Enter meeting title..."
                                            value={scheduleTitle}
                                            onChange={(e) => setScheduleTitle(e.target.value)}
                                            className={`w-full bg-white border ${scheduleFormErrors.title ? 'border-red-500' : 'border-slate-200'} rounded-lg px-3 py-2 text-xs text-slate-700 outline-none focus:border-blue-500 shadow-sm transition-colors`}
                                        />
                                        {scheduleFormErrors.title && (
                                            <p className="text-[11px] font-bold text-red-500 mt-1">
                                                {scheduleFormErrors.title}
                                            </p>
                                        )}
                                    </div>
                                    {/* Date and Time Picker Row */}

                                    <div className="flex flex-wrap items-end gap-4 text-xs text-slate-650">
                                        <div className="flex flex-col gap-1">
                                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                                Date <span className="text-red-500 ml-0.5">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                value={scheduleDate}
                                                onChange={(e) => setScheduleDate(e.target.value)}
                                                className={`bg-white border ${scheduleFormErrors.date ? 'border-red-500' : 'border-slate-200'} rounded-lg px-3 py-1.5 focus:border-blue-500 outline-none text-slate-700 text-xs cursor-pointer shadow-sm hover:border-slate-350 transition-colors`}
                                            />
                                            {scheduleFormErrors.date && (
                                                <p className="text-[9px] text-red-500 font-semibold mt-0.5">{scheduleFormErrors.date}</p>
                                            )}
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                                Start Time <span className="text-red-500 ml-0.5">*</span>
                                            </label>
                                            <input
                                                type="time"
                                                value={scheduleStartTime}
                                                onChange={(e) => setScheduleStartTime(e.target.value)}
                                                className={`bg-white border ${scheduleFormErrors.start_time ? 'border-red-500' : 'border-slate-200'} rounded-lg px-3 py-1.5 focus:border-blue-500 outline-none text-slate-700 text-xs cursor-pointer shadow-sm hover:border-slate-350 transition-colors`}
                                            />
                                            {scheduleFormErrors.start_time && (
                                                <p className="text-[9px] text-red-500 font-semibold mt-0.5">{scheduleFormErrors.start_time}</p>
                                            )}
                                        </div>
                                        {scheduleFormErrors.time && (
                                            <p className="text-xs text-red-500 font-semibold w-full mt-1">
                                                {scheduleFormErrors.time}
                                            </p>
                                        )}
                                    </div>
                                    {/* Detailed Fields */}

                                    <div className="space-y-4">
                                        {/* Meeting Type Selection */}
                                         <div className="flex items-start gap-4">
                                             <svg className="w-5 h-5 text-slate-400 mt-2.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                                             <div className="flex-grow">
                                                 <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 font-sans">
                                                     Meeting Type <span className="text-red-500 ml-0.5">*</span>
                                                 </label>
                                                 <MeetingTypeSelect
                                                     value={scheduleType}
                                                     onChange={(e) => setScheduleType(e.target.value)}
                                                     className={`bg-white border ${scheduleFormErrors.schedule_type ? 'border-red-500' : 'border-slate-200'} rounded-lg px-3 py-2 outline-none text-slate-700 text-xs w-full focus:border-blue-500 cursor-pointer shadow-sm hover:border-slate-350 transition-colors`}
                                                 />
                                                 {scheduleFormErrors.schedule_type && (
                                                     <p className="text-[9px] text-red-500 font-bold mt-1">{scheduleFormErrors.schedule_type}</p>
                                                 )}
                                             </div>
                                         </div>

                                         {/* Admission Control Selection for Zoom & Google Meet */}
                                         <div className="flex items-start gap-4">
                                             <svg className="w-5 h-5 text-slate-400 mt-2.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751A11.959 11.959 0 0112 2.714z" /></svg>
                                             <div className="flex-grow">
                                                 <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Participant Admission Control</label>
                                                 <div className="grid grid-cols-2 gap-2">
                                                     <label className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${scheduleAdmissionControl === 'admit' ? 'border-main bg-red-50/60 text-slate-900 font-extrabold shadow-xs' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}>
                                                         <input
                                                             type="radio"
                                                             name="modalAdmissionControlForm"
                                                             value="admit"
                                                             checked={scheduleAdmissionControl === 'admit'}
                                                             onChange={() => setScheduleAdmissionControl('admit')}
                                                             className="text-main focus:ring-0 cursor-pointer"
                                                         />
                                                         <div className="text-[11px]">
                                                             <span className="block font-bold">Host Admit Required</span>
                                                             <span className="text-[9px] text-slate-400 font-normal">Waiting room / admit</span>
                                                         </div>
                                                     </label>
                                                     <label className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${scheduleAdmissionControl === 'direct' ? 'border-main bg-red-50/60 text-slate-900 font-extrabold shadow-xs' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}>
                                                         <input
                                                             type="radio"
                                                             name="modalAdmissionControlForm"
                                                             value="direct"
                                                             checked={scheduleAdmissionControl === 'direct'}
                                                             onChange={() => setScheduleAdmissionControl('direct')}
                                                             className="text-main focus:ring-0 cursor-pointer"
                                                         />
                                                         <div className="text-[11px]">
                                                             <span className="block font-bold">Direct Join</span>
                                                             <span className="text-[9px] text-slate-400 font-normal">No host admit needed</span>
                                                         </div>
                                                     </label>
                                                 </div>
                                             </div>
                                         </div>

                                        {/* CONDITIONAL FIELDS: Physical Meetings */}

                                        {scheduleType === 'physical' && (
                                            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-4 animate-fadeIn">
                                                <h4 className="text-xs font-black text-slate-700 uppercase tracking-wider">Physical Venue Configuration</h4>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">Building *</label>
                                                        <input
                                                            type="text"
                                                            placeholder="HQ Block A"
                                                            value={scheduleBuilding}
                                                            onChange={(e) => setScheduleBuilding(e.target.value)}
                                                            className={`w-full bg-white border ${scheduleFormErrors.building ? 'border-red-500' : 'border-slate-200'} rounded-lg px-2.5 py-1.5 text-xs text-slate-700 focus:border-blue-500 outline-none`}
                                                        />
                                                        {scheduleFormErrors.building && <p className="text-[9px] text-red-500 font-bold mt-0.5">{scheduleFormErrors.building}</p>}
                                                    </div>
                                                    <div>
                                                        <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">Floor *</label>
                                                        <input
                                                            type="text"
                                                            placeholder="Floor 4"
                                                            value={scheduleFloor}
                                                            onChange={(e) => setScheduleFloor(e.target.value)}
                                                            className={`w-full bg-white border ${scheduleFormErrors.floor ? 'border-red-500' : 'border-slate-200'} rounded-lg px-2.5 py-1.5 text-xs text-slate-700 focus:border-blue-500 outline-none`}
                                                        />
                                                        {scheduleFormErrors.floor && <p className="text-[9px] text-red-500 font-bold mt-0.5">{scheduleFormErrors.floor}</p>}
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">Conference Room Name *</label>
                                                        <select
                                                            value={scheduleRoomName}
                                                            onChange={(e) => setScheduleRoomName(e.target.value)}
                                                            className={`w-full bg-white border ${scheduleFormErrors.room_name ? 'border-red-500' : 'border-slate-200'} rounded-lg px-2.5 py-1.5 text-xs text-slate-700 focus:border-blue-500 outline-none cursor-pointer`}
                                                        >
                                                            <option value="Alpha Suite">Alpha Suite</option>
                                                            <option value="Omega Room">Omega Room</option>
                                                            <option value="Executive Boardroom">Executive Boardroom</option>
                                                            <option value="Innovations Lounge">Innovations Lounge</option>
                                                            <option value="Tech Lab 3">Tech Lab 3</option>
                                                        </select>
                                                        {scheduleFormErrors.room_name && <p className="text-[9px] text-red-500 font-bold mt-0.5">{scheduleFormErrors.room_name}</p>}
                                                    </div>
                                                    <div>
                                                        <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">Seating Capacity *</label>
                                                        <input
                                                            type="number"
                                                            value={scheduleSeatingCapacity}
                                                            onChange={(e) => setScheduleSeatingCapacity(e.target.value)}
                                                            className={`w-full bg-white border ${scheduleFormErrors.seating_capacity ? 'border-red-500' : 'border-slate-200'} rounded-lg px-2.5 py-1.5 text-xs text-slate-700 focus:border-blue-500 outline-none`}
                                                        />
                                                        {scheduleFormErrors.seating_capacity && <p className="text-[9px] text-red-500 font-bold mt-0.5">{scheduleFormErrors.seating_capacity}</p>}
                                                    </div>
                                                </div>
                                                <p className="text-[10px] text-amber-600 font-semibold bg-amber-500/10 p-2 rounded-lg border border-amber-500/20">
                                                    * This physical venue allocation will automatically validate availability to prevent double-booking.
                                                </p>
                                            </div>
                                        )}

                                        {/* Recurrence Setup */}

                                        <div className="flex items-start gap-4">
                                            <svg className="w-5 h-5 text-slate-400 mt-2.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89M9 11l3 3L22 4" /></svg>
                                            <div className="flex-grow">
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Meeting Recurrence</label>
                                                <div className="relative">
                                                    <select
                                                        value={scheduleRecurring}
                                                        onChange={(e) => setScheduleRecurring(e.target.value)}
                                                        className="bg-white border border-slate-200 rounded-lg px-3 py-2 pr-8 outline-none text-slate-700 text-xs w-full focus:border-blue-500 cursor-pointer shadow-sm hover:border-slate-350 transition-colors appearance-none"
                                                    >
                                                        <option value="none">Does not repeat</option>
                                                        <option value="daily">Daily recurrence</option>
                                                        <option value="weekly">Weekly recurrence</option>
                                                        <option value="monthly">Monthly recurrence</option>
                                                    </select>
                                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-slate-400">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Calendar Creator displaying Owner */}

                                        <div className="flex items-center gap-4 text-xs text-slate-650">
                                            <div className="w-5 h-5 rounded-full bg-blue-500 flex-shrink-0 flex items-center justify-center font-bold text-white text-[10px]">
                                                {user?.first_name ? user.first_name[0].toUpperCase() : 'S'}

                                            </div>
                                            <span>{user?.first_name ? `${user.first_name} ${user.last_name || ''}` : 'sowndar moorthy'}</span>
                                        </div>
                                        {/* Description Textarea */}

                                        <div className="flex items-start gap-4">
                                            <svg className="w-5 h-5 text-slate-400 mt-2 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h7" /></svg>
                                            <div className="flex-grow space-y-0.5">
                                                <div className="flex items-center gap-3 bg-slate-50 px-3 py-1.5 rounded-t-lg border border-slate-200 border-b-0 text-slate-500 text-xs">
                                                    <span className="font-bold cursor-pointer hover:text-slate-700">B</span>
                                                    <span className="italic cursor-pointer hover:text-slate-700">I</span>
                                                    <span className="underline cursor-pointer hover:text-slate-700">U</span>
                                                    <span className="cursor-pointer hover:text-slate-700 ml-2 text-sm leading-none">≡</span>
                                                </div>
                                                <textarea
                                                    placeholder="Add description"
                                                    value={scheduleDescription}

                                                    onChange={(e) => setScheduleDescription(e.target.value)}

                                                    className="bg-white border border-slate-200 rounded-b-lg p-3 outline-none text-xs text-slate-700 w-full h-24 focus:border-blue-500 placeholder-slate-400 resize-none shadow-sm"
                                                />
                                            </div>
                                        </div>
                                        {/* Meeting Visibility & Attendees Options */}

                                        <div className="space-y-4 bg-slate-50 p-4 rounded-xl border border-slate-200 text-[11px] mt-2">
                                            <label className="block font-bold text-slate-500 uppercase tracking-wider mb-1 text-[10px]">
                                                Visibility & Attendance
                                            </label>
                                            <div className="space-y-3">
                                                {/* Option 2: Selective Employees */}

                                                <label className="flex items-start gap-2.5 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        name="targetType"
                                                        value="selective_employees"
                                                        checked={targetType.includes('selective_employees')}

                                                        onChange={(e) => {
                                                            if (e.target.checked) setTargetType(prev => [...prev, 'selective_employees']);
                                                            else setTargetType(prev => prev.filter(v => v !== 'selective_employees'));
                                                        }}

                                                        className="mt-0.5 rounded border-slate-350 bg-white text-blue-500 focus:ring-0 w-3.5 h-3.5 cursor-pointer"
                                                    />
                                                    <div className="-mt-0.5">
                                                        <span className="font-semibold text-slate-750">Internal Employees (Selective)</span>
                                                        <p className="text-[10px] text-slate-400">Only visible to chosen employees on their calendar & dashboard.</p>
                                                    </div>
                                                </label>
                                                {/* Employee Selection Dropdown / Checkbox List */}

                                                {targetType.includes('selective_employees') && (
                                                    <div className="mt-1 animate-fadeIn">
                                                        <div className={`bg-white rounded-lg border ${scheduleFormErrors.selective_employees ? 'border-red-500' : 'border-slate-200'} overflow-hidden flex flex-col`}>
                                                            <div className="sticky top-0 bg-white z-10 border-b border-slate-100 p-2 flex items-center gap-2">
                                                                <input
                                                                    type="text"
                                                                    placeholder="Search employees..."
                                                                    value={employeeSearchQuery}
                                                                    onChange={(e) => setEmployeeSearchQuery(e.target.value)}
                                                                    className="flex-1 min-w-0 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-700 focus:border-blue-500 focus:bg-white outline-none placeholder-slate-400"
                                                                />
                                                                {allEmployeesList.length > 0 && (
                                                                    <label className="flex items-center gap-1.5 cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5 shrink-0 whitespace-nowrap">
                                                                        <span className="text-slate-700 text-[10px] font-bold uppercase tracking-wider">Select All</span>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={(() => {
                                                                                const filtered = allEmployeesList.filter(emp =>
                                                                                    emp.name.toLowerCase().includes(employeeSearchQuery.toLowerCase()) ||
                                                                                    (emp.email && emp.email.toLowerCase().includes(employeeSearchQuery.toLowerCase()))
                                                                                );
                                                                                return filtered.length > 0 && filtered.every(emp => selectedEmployees.includes(emp.user_id));
                                                                            })()}
                                                                            onChange={(e) => {
                                                                                const filteredEmployeesForSelection = allEmployeesList.filter(emp =>
                                                                                    emp.name.toLowerCase().includes(employeeSearchQuery.toLowerCase()) ||
                                                                                    (emp.email && emp.email.toLowerCase().includes(employeeSearchQuery.toLowerCase()))
                                                                                );
                                                                                if (e.target.checked) {
                                                                                    const newSelected = [...selectedEmployees];
                                                                                    filteredEmployeesForSelection.forEach(emp => {
                                                                                        if (!newSelected.includes(emp.user_id)) newSelected.push(emp.user_id);
                                                                                    });
                                                                                    setSelectedEmployees(newSelected);
                                                                                } else {
                                                                                    const filteredIds = filteredEmployeesForSelection.map(emp => emp.user_id);
                                                                                    setSelectedEmployees(prev => prev.filter(id => !filteredIds.includes(id)));
                                                                                }
                                                                            }}
                                                                            className="rounded-sm border-slate-350 bg-white text-blue-500 focus:ring-0 w-3.5 h-3.5 cursor-pointer"
                                                                        />
                                                                    </label>
                                                                )}
                                                            </div>
                                                            <div className="max-h-[165px] overflow-y-auto custom-scrollbar px-2 pb-2 pt-1 space-y-0.5">
                                                                {(() => {
                                                                    const filteredEmployeesForSelection = allEmployeesList
                                                                        .filter(emp =>
                                                                            emp.name.toLowerCase().includes(employeeSearchQuery.toLowerCase()) ||
                                                                            (emp.email && emp.email.toLowerCase().includes(employeeSearchQuery.toLowerCase()))
                                                                        )
                                                                        .sort((a, b) => {
                                                                            const aSelected = selectedEmployees.includes(a.user_id) ? 0 : 1;
                                                                            const bSelected = selectedEmployees.includes(b.user_id) ? 0 : 1;
                                                                            if (aSelected !== bSelected) return aSelected - bSelected;
                                                                            return a.name.localeCompare(b.name);
                                                                        });

                                                                    if (allEmployeesList.length === 0) {
                                                                        return <span className="text-[10px] text-slate-500">Loading employees...</span>;
                                                                    }
                                                                    if (filteredEmployeesForSelection.length === 0) {
                                                                        return <span className="text-[10px] text-slate-400 block p-1">No employees found</span>;
                                                                    }
                                                                    return filteredEmployeesForSelection.map(emp => {
                                                                        const isSelected = selectedEmployees.includes(emp.user_id);
                                                                        return (
                                                                            <label
                                                                                key={emp.user_id}
                                                                                className={`flex items-center justify-between cursor-pointer py-1.5 px-2 rounded-md transition-colors ${isSelected ? 'bg-blue-50 text-blue-800' : 'hover:bg-slate-50 text-slate-700'}`}
                                                                            >
                                                                                <span className={`text-[11px] ${isSelected ? 'font-semibold' : ''}`}>{emp.name}</span>
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={isSelected}
                                                                                    onChange={(e) => {
                                                                                        if (e.target.checked) {
                                                                                            setSelectedEmployees(prev => [...prev, emp.user_id]);
                                                                                        } else {
                                                                                            setSelectedEmployees(prev => prev.filter(id => id !== emp.user_id));
                                                                                        }
                                                                                    }}
                                                                                    className="rounded-sm border-slate-350 bg-white text-blue-500 focus:ring-0 w-3.5 h-3.5 cursor-pointer"
                                                                                />
                                                                            </label>
                                                                        );
                                                                    });
                                                                })()}
                                                            </div>
                                                        </div>
                                                        {scheduleFormErrors.selective_employees && (
                                                            <p className="text-[10px] text-red-500 font-bold mt-1">
                                                                {scheduleFormErrors.selective_employees}
                                                            </p>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Option 3: External Guests (Email) */}
                                                <label className="flex items-start gap-2.5 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        name="targetType"
                                                        value="customers"
                                                        checked={targetType.includes('customers')}

                                                        onChange={(e) => {
                                                            if (e.target.checked) setTargetType(prev => [...prev, 'customers']);
                                                            else setTargetType(prev => prev.filter(v => v !== 'customers'));
                                                        }}

                                                        className="mt-0.5 rounded border-slate-350 bg-white text-blue-500 focus:ring-0 w-3.5 h-3.5 cursor-pointer"
                                                    />
                                                    <div className="-mt-0.5">
                                                        <span className="font-semibold text-slate-750">External Guests (Email)</span>
                                                        <p className="text-[10px] text-slate-400">Send an email invitation with the meeting link directly.</p>
                                                    </div>
                                                </label>

                                                {targetType.includes('customers') && (
                                                    <div className="pl-6 mt-1 animate-fadeIn">
                                                        <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">Guest Emails</label>
                                                        <textarea
                                                            placeholder={"john@example.com\nalice@example.com"}
                                                            value={manualEmails}
                                                            onChange={(e) => setManualEmails(e.target.value)}
                                                            className="bg-white border border-slate-200 rounded-lg p-2 outline-none text-slate-700 text-[11px] w-full h-16 focus:border-blue-500 placeholder-slate-400 resize-none shadow-sm"
                                                        />
                                                        <p className="text-[9px] text-slate-400 mt-1">Separate emails with comma or new line</p>
                                                        {scheduleFormErrors.manual_emails && (
                                                            <p className="text-[10px] text-red-500 font-bold mt-1">
                                                                {scheduleFormErrors.manual_emails}
                                                            </p>
                                                        )}
                                                    </div>
                                                )}

                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Action Buttons (Fixed Bottom) */}
                                <div className="px-6 py-4 flex justify-end items-center gap-3 bg-white shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => setShowScheduleFormModal(false)}
                                        className="px-5 py-2 rounded-full text-xs font-bold text-slate-600 bg-white border border-slate-300 hover:bg-slate-50 transition-colors shadow-sm"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="bg-main hover:bg-mainHover text-white font-extrabold px-6 py-2 rounded-full text-xs shadow-md transition-all"
                                    >
                                        Save
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Modal for Calendar scheduling success */}

                {showCalendarModal && (
                    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                        <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl border border-slate-200 text-center space-y-4">
                            <div className="flex justify-center">
                                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
                                </div>
                            </div>
                            <h3 className="text-base font-extrabold text-slate-900">Meeting Scheduled</h3>
                            <p className="text-xs text-slate-500 leading-relaxed">
                                This meeting has been scheduled in your workspace collaboration calendar.
                            </p>
                            <button
                                onClick={() => setShowCalendarModal(false)}

                                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold w-full py-2.5 rounded-xl transition-all text-xs text-center shadow-md shadow-emerald-600/15"
                            >
                                OK
                            </button>
                        </div>
                    </div>
                )}

                {/* Custom Resolution Notes Popup */}
                {resolutionPopup.show && (
                    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-fadeIn">
                        <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl border border-slate-200 text-left space-y-4">
                            <div className="flex items-center gap-3 text-slate-800">
                                <svg className="w-6 h-6 text-main flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01m-.01 4h.01" /></svg>
                                <h3 className="text-base font-extrabold text-slate-950 font-sans">Resolve Escalation</h3>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Resolution Notes *</label>
                                <textarea
                                    value={resolutionPopup.notes}
                                    onChange={(e) => setResolutionPopup(prev => ({ ...prev, notes: e.target.value }))}
                                    placeholder="Enter details on how the schedule capacity or conflict was resolved..."
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs outline-none focus:border-main focus:bg-white text-slate-700 min-h-[90px] resize-none transition-all"
                                />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={() => setResolutionPopup({ show: false, escalationId: null, notes: '' })}
                                    className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold flex-1 py-2.5 rounded-xl transition-all text-xs text-center border border-slate-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={submitEscalationResolution}
                                    className="bg-main hover:bg-mainHover text-white font-bold flex-1 py-2.5 rounded-xl transition-all text-xs text-center shadow-md shadow-main/15"
                                >
                                    Resolve
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Edit Meeting Modal */}
                {editMeetingModal.show && (
                    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-fadeIn">
                        <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl border border-slate-200 text-left space-y-4 max-h-[90vh] flex flex-col">
                            <div className="flex items-center justify-between border-b border-slate-100 pb-3 shrink-0">
                                <h3 className="text-sm font-extrabold text-slate-950 font-sans">Edit Meeting Schedule</h3>
                                <button 
                                    onClick={() => setEditMeetingModal({ show: false, meetingId: null, title: '', date: '', startTime: '', selectedEmployees: [], manualEmails: '', searchEmpQuery: '', errors: {} })}
                                    className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-100 rounded-full transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>

                            <div className="space-y-4 overflow-y-auto custom-scrollbar pr-1 flex-1">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Title *</label>
                                    <input
                                        type="text"
                                        value={editMeetingModal.title}
                                        onChange={(e) => setEditMeetingModal(prev => ({ ...prev, title: e.target.value }))}
                                        className={`w-full bg-slate-50 border ${editMeetingModal.errors.title ? 'border-red-500' : 'border-slate-200'} rounded-xl px-3 py-2 text-xs outline-none focus:border-main focus:bg-white text-slate-700 transition-all`}
                                    />
                                    {editMeetingModal.errors.title && (
                                        <p className="text-[9px] text-red-500 font-semibold mt-0.5">{editMeetingModal.errors.title}</p>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Date *</label>
                                        <input
                                            type="date"
                                            value={editMeetingModal.date}
                                            onChange={(e) => setEditMeetingModal(prev => ({ ...prev, date: e.target.value }))}
                                            className={`w-full bg-slate-50 border ${editMeetingModal.errors.date ? 'border-red-500' : 'border-slate-200'} rounded-xl px-3 py-2 text-xs outline-none focus:border-main focus:bg-white text-slate-700 transition-all`}
                                        />
                                        {editMeetingModal.errors.date && (
                                            <p className="text-[9px] text-red-500 font-semibold mt-0.5">{editMeetingModal.errors.date}</p>
                                        )}
                                    </div>

                                    <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Start Time *</label>
                                        <input
                                            type="time"
                                            value={editMeetingModal.startTime}
                                            onChange={(e) => setEditMeetingModal(prev => ({ ...prev, startTime: e.target.value }))}
                                            className={`w-full bg-slate-50 border ${editMeetingModal.errors.startTime ? 'border-red-500' : 'border-slate-200'} rounded-xl px-3 py-2 text-xs outline-none focus:border-main focus:bg-white text-slate-700 transition-all`}
                                        />
                                        {editMeetingModal.errors.startTime && (
                                            <p className="text-[9px] text-red-500 font-semibold mt-0.5">{editMeetingModal.errors.startTime}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Internal Employees Section */}
                                <div className="space-y-2 border-t border-slate-100 pt-3">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Invite Internal Employees</label>
                                    <input
                                        type="text"
                                        placeholder="Search employees by name or email..."
                                        value={editMeetingModal.searchEmpQuery || ''}
                                        onChange={(e) => setEditMeetingModal(prev => ({ ...prev, searchEmpQuery: e.target.value }))}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs text-slate-700 outline-none focus:border-main focus:bg-white transition-all placeholder-slate-400"
                                    />
                                    <div className="bg-slate-50 rounded-xl border border-slate-200 max-h-36 overflow-y-auto custom-scrollbar p-2 space-y-1">
                                        {(() => {
                                            const query = (editMeetingModal.searchEmpQuery || '').toLowerCase();
                                            const filtered = allEmployeesList.filter(emp =>
                                                emp.name.toLowerCase().includes(query) ||
                                                (emp.email && emp.email.toLowerCase().includes(query))
                                            );
                                            const currentSel = editMeetingModal.selectedEmployees || [];
                                            const isAllSelected = filtered.length > 0 && filtered.every(emp => currentSel.includes(emp.user_id));

                                            return (
                                                <>
                                                    {filtered.length > 0 && (
                                                        <div className="pb-1 mb-1 border-b border-slate-200/60 flex items-center justify-between px-1">
                                                            <span className="text-[10px] font-bold text-slate-500">Select All Filtered</span>
                                                            <input
                                                                type="checkbox"
                                                                checked={isAllSelected}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        const added = new Set([...currentSel]);
                                                                        filtered.forEach(emp => added.add(emp.user_id));
                                                                        setEditMeetingModal(prev => ({ ...prev, selectedEmployees: Array.from(added) }));
                                                                    } else {
                                                                        const filterIds = new Set(filtered.map(emp => emp.user_id));
                                                                        setEditMeetingModal(prev => ({ ...prev, selectedEmployees: currentSel.filter(id => !filterIds.has(id)) }));
                                                                    }
                                                                }}
                                                                className="rounded-sm border-slate-300 text-main focus:ring-0 w-3.5 h-3.5 cursor-pointer"
                                                            />
                                                        </div>
                                                    )}
                                                    {filtered.length === 0 ? (
                                                        <p className="text-[10px] text-slate-400 text-center py-2">No employees found</p>
                                                    ) : (
                                                        filtered.map(emp => {
                                                            const isChecked = currentSel.includes(emp.user_id);
                                                            return (
                                                                <label key={emp.user_id} className="flex items-center justify-between hover:bg-white p-1.5 rounded-lg cursor-pointer transition-colors">
                                                                    <div className="flex flex-col">
                                                                        <span className="text-xs font-semibold text-slate-700">{emp.name}</span>
                                                                        <span className="text-[10px] text-slate-400">{emp.email}</span>
                                                                    </div>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isChecked}
                                                                        onChange={(e) => {
                                                                            if (e.target.checked) {
                                                                                setEditMeetingModal(prev => ({ ...prev, selectedEmployees: [...(prev.selectedEmployees || []), emp.user_id] }));
                                                                            } else {
                                                                                setEditMeetingModal(prev => ({ ...prev, selectedEmployees: (prev.selectedEmployees || []).filter(id => id !== emp.user_id) }));
                                                                            }
                                                                        }}
                                                                        className="rounded-sm border-slate-300 text-main focus:ring-0 w-3.5 h-3.5 cursor-pointer"
                                                                    />
                                                                </label>
                                                            );
                                                        })
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>

                                {/* External Guests Section */}
                                <div className="space-y-1 border-t border-slate-100 pt-3">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">External Guest Emails</label>
                                    <textarea
                                        placeholder="Enter guest emails separated by commas (e.g. guest@company.com, client@domain.com)"
                                        value={editMeetingModal.manualEmails || ''}
                                        onChange={(e) => setEditMeetingModal(prev => ({ ...prev, manualEmails: e.target.value }))}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs text-slate-700 outline-none focus:border-main focus:bg-white transition-all placeholder-slate-400 h-16 resize-none"
                                    />
                                    <p className="text-[9px] text-slate-400">Only invited email addresses will be permitted to join this meeting session.</p>
                                </div>
                            </div>

                            <div className="flex gap-3 pt-3 border-t border-slate-100 shrink-0">
                                <button
                                    onClick={() => setEditMeetingModal({ show: false, meetingId: null, title: '', date: '', startTime: '', selectedEmployees: [], manualEmails: '', searchEmpQuery: '', errors: {} })}
                                    className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold flex-1 py-2.5 rounded-xl transition-all text-xs text-center border border-slate-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={submitEditMeeting}
                                    className="bg-main hover:bg-mainHover text-white font-bold flex-1 py-2.5 rounded-xl transition-all text-xs text-center shadow-md shadow-main/15"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Custom Alert Popup */}

                {alertPopup.show && (
                    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[210] p-4 animate-fadeIn">
                        <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl border border-slate-200 text-left space-y-4">
                            <div className={`flex items-center gap-3 ${
                                (alertPopup.title?.toLowerCase().includes('copied') || alertPopup.title?.toLowerCase().includes('success'))
                                    ? 'text-emerald-650'
                                    : 'text-red-505'
                            }`}>
                                {(alertPopup.title?.toLowerCase().includes('copied') || alertPopup.title?.toLowerCase().includes('success')) ? (
                                    <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                ) : (
                                    <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                )}
                                <h3 className="text-base font-extrabold text-slate-950">{alertPopup.title}</h3>
                            </div>
                            <p className="text-xs text-slate-600 leading-relaxed font-semibold">
                                {alertPopup.message}

                            </p>
                            <button
                                onClick={() => setAlertPopup({ show: false, message: '', title: 'Notification' })}

                                className="bg-main hover:bg-mainHover text-white font-bold w-full py-2.5 rounded-xl transition-all text-xs text-center shadow-md shadow-main/15"
                            >
                                Okay
                            </button>
                        </div>
                    </div>
                )}

                {/* Custom Confirm Popup */}

                {confirmPopup.show && (
                    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4 animate-fadeIn">
                        <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl border border-slate-200 text-left space-y-4">
                            <div className="flex items-center gap-3 text-slate-800">
                                <svg className="w-6 h-6 text-yellow-505 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                                <h3 className="text-base font-extrabold text-slate-950 font-sans">{confirmPopup.title || 'Confirm'}</h3>
                            </div>
                            <p className="text-xs text-slate-600 leading-relaxed font-semibold">
                                {confirmPopup.message}

                            </p>
                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={() => setConfirmPopup({ show: false, message: '', title: '', onConfirm: null })}

                                    className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold flex-1 py-2.5 rounded-xl transition-all text-xs text-center border border-slate-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (confirmPopup.onConfirm) confirmPopup.onConfirm();
                                        setConfirmPopup({ show: false, message: '', title: '', onConfirm: null });
                                    }}

                                    className="bg-[#c41230] hover:bg-[#a00f27] text-white font-bold flex-1 py-2.5 rounded-xl transition-all text-xs text-center shadow-md shadow-red-500/15"
                                >
                                    {confirmPopup.confirmText || (confirmPopup.title?.toLowerCase().includes('end') ? 'End' : 'Delete')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Provider Admin Modal */}

                {showProviderAdmin && (
                    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4 animate-fadeIn">
                        <div className="bg-white rounded-[2rem] w-full max-w-5xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                            {/* Header */}

                            <div className="bg-main px-8 py-5 flex items-center justify-between shrink-0">
                                <div className="flex items-center gap-3">
                                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                    </span>
                                    <div>
                                        <h2 className="text-lg font-black text-white">Meeting Provider Accounts</h2>
                                        <p className="text-[10px] text-red-100/80 font-semibold uppercase tracking-wider">Configure Zoom and Google Meet automated scheduling limits</p>
                                    </div>
                                </div>
                                <button onClick={() => setShowProviderAdmin(false)} className="text-red-200 hover:text-white transition-colors bg-black/10 hover:bg-black/20 rounded-full p-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                            <div className="flex-grow overflow-y-auto p-8 bg-white space-y-6">
                                {providerError && (
                                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-xs font-bold font-sans flex items-center justify-between shadow-sm animate-fadeIn">
                                        <div className="flex items-center gap-2">
                                            <svg className="w-4 h-4 text-red-500 shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                                            <span>{providerError}</span>
                                        </div>
                                        <button onClick={() => setProviderError('')} className="text-red-500 hover:text-red-700 font-black text-sm ml-2">×</button>
                                    </div>
                                )}
                                {showProviderForm ? (
                                    <div className="space-y-5 animate-fadeIn">
                                        <div className="flex justify-between items-center pb-4 border-b border-slate-100">
                                            <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider">
                                                {editingProviderId ? 'Edit Provider Account' : 'Add Provider Account'}

                                            </h3>
                                            <button
                                                onClick={() => setShowProviderForm(false)}

                                                className="text-slate-500 hover:text-slate-800 transition-colors flex items-center gap-1 text-xs font-bold"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                                                Back to Accounts
                                            </button>
                                        </div>
                                        <div className="space-y-5">
                                            <div className="grid grid-cols-2 gap-5">
                                                <div>
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Provider Type</label>
                                                    <select
                                                        value={providerFormData.provider}

                                                        onChange={(e) => setProviderFormData({...providerFormData, provider: e.target.value})}

                                                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-700 font-semibold focus:border-main focus:ring-1 focus:ring-main outline-none transition-all"
                                                    >
                                                        <option value="zoom">Zoom</option>
                                                        <option value="google_meet">Google Meet</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Account Label</label>
                                                    <input
                                                        type="text"
                                                        placeholder="e.g. Sales Team Zoom"
                                                        value={providerFormData.account_label}

                                                        onChange={(e) => setProviderFormData({...providerFormData, account_label: e.target.value})}

                                                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-700 font-semibold focus:border-main focus:ring-1 focus:ring-main outline-none transition-all"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Account Email</label>
                                                    <input
                                                        type="email"
                                                        placeholder="admin@company.com"
                                                        value={providerFormData.email}

                                                        onChange={(e) => setProviderFormData({...providerFormData, email: e.target.value})}

                                                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-700 font-semibold focus:border-main focus:ring-1 focus:ring-main outline-none transition-all"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Max Concurrent Meetings</label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={providerFormData.max_concurrent}

                                                        onChange={(e) => setProviderFormData({...providerFormData, max_concurrent: parseInt(e.target.value) || 1})}

                                                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-700 font-semibold focus:border-main focus:ring-1 focus:ring-main outline-none transition-all"
                                                    />
                                                </div>
                                            </div>
                                            <div className="pt-5 mt-5 border-t border-slate-100">
                                                <h4 className="text-[10px] font-black text-slate-600 uppercase tracking-wider mb-4">API Credentials</h4>
                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Client ID (OAuth)</label>
                                                        <input
                                                            type="text"
                                                            value={providerFormData.client_id || ''}

                                                            onChange={(e) => setProviderFormData({...providerFormData, client_id: e.target.value})}

                                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-700 font-mono focus:border-main focus:ring-1 focus:ring-main outline-none transition-all"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Client Secret (OAuth)</label>
                                                        <input
                                                            type="password"
                                                            value={providerFormData.client_secret || ''}

                                                            onChange={(e) => setProviderFormData({...providerFormData, client_secret: e.target.value})}

                                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-700 font-mono focus:border-main focus:ring-1 focus:ring-main outline-none transition-all"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Account / App ID (Zoom Account ID or GCalendar ID)</label>
                                                        <input
                                                            type="text"
                                                            value={providerFormData.account_id || ''}

                                                            onChange={(e) => setProviderFormData({...providerFormData, account_id: e.target.value})}

                                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-700 font-mono focus:border-main focus:ring-1 focus:ring-main outline-none transition-all"
                                                        />
                                                    </div>
                                                    {providerFormData.provider === 'google_meet' && (
                                                        <div>
                                                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Refresh Token (Google OAuth)</label>
                                                            <input
                                                                type="text"
                                                                value={providerFormData.refresh_token || ''}

                                                                onChange={(e) => setProviderFormData({...providerFormData, refresh_token: e.target.value})}

                                                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-700 font-mono focus:border-main focus:ring-1 focus:ring-main outline-none transition-all"
                                                            />
                                                        </div>
                                                    )}

                                                </div>
                                            </div>
                                            <div className="pt-6 mt-6 border-t border-slate-100 flex justify-end gap-3">
                                                <button
                                                    onClick={() => setShowProviderForm(false)}

                                                    className="px-6 py-2.5 text-xs font-bold text-slate-600 hover:text-slate-800 transition-colors"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={handleSaveProviderAccount}

                                                    className="bg-main hover:bg-mainHover text-white font-bold px-6 py-2.5 rounded-xl transition-colors text-xs shadow-md shadow-main/20"
                                                >
                                                    {editingProviderId ? 'Update Account' : 'Save Account'}

                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {/* Escalations Section */}

                                        {escalations.length > 0 && (
                                            <section>
                                                <div className="flex items-center gap-2 mb-4">
                                                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                                                    <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider">Active Escalations ({escalations.length})</h3>
                                                </div>
                                                <div className="space-y-3">
                                                    {escalations.map(esc => (
                                                        <div key={esc.id} className="bg-red-50 border border-red-100 rounded-xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                                                            <div>
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <span className="bg-red-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase">{esc.provider}</span>
                                                                    <span className="text-[10px] text-red-700 font-semibold">{new Date(esc.created_at).toLocaleString()} by {esc.requested_by_name || esc.requested_by}</span>
                                                                </div>
                                                                <p className="text-xs text-red-900 font-medium">{esc.message}</p>
                                                            </div>
                                                            <button
                                                                onClick={() => handleResolveEscalation(esc.id)}

                                                                className="shrink-0 bg-red-100 hover:bg-red-200 text-red-700 text-xs font-bold px-4 py-2 rounded-lg transition-colors border border-red-200"
                                                            >
                                                                Mark Resolved
                                                            </button>
                                                        </div>
                                                    ))}

                                                </div>
                                            </section>
                                        )}

                                        {/* Accounts Section */}

                                        <section>
                                            <div className="flex items-center justify-between mb-4">
                                                <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider">Configured Accounts</h3>
                                                <button
                                                    onClick={() => {
                                                        setProviderFormData({
                                                            provider: 'zoom', account_label: '', email: '', client_id: '', client_secret: '',
                                                            account_id: '', api_key: '', api_secret: '', refresh_token: '',
                                                            max_concurrent: 1, subscription_tier: 'pro', notes: ''
                                                        });
                                                        setEditingProviderId(null);
                                                        setShowProviderForm(true);
                                                    }}

                                                    className="bg-main hover:bg-mainHover text-white text-[10px] font-bold uppercase tracking-wider px-4 py-2.5 rounded-xl flex items-center gap-2 shadow-sm transition-all"
                                                >
                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                                                    Add Account
                                                </button>
                                            </div>
                                            {providerAccounts.length === 0 ? (
                                                <div className="text-center py-12 bg-white border border-slate-200 border-dashed rounded-2xl">
                                                    <p className="text-sm font-bold text-slate-600 mb-2">No provider accounts configured</p>
                                                    <p className="text-[11px] text-slate-400 max-w-md mx-auto">Add a Zoom or Google Meet API account to enable automatic meeting scheduling. Collab will load-balance across available accounts automatically.</p>
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {providerAccounts.map(acc => (
                                                        <div key={acc.id} className="bg-white border border-slate-200 p-5 rounded-2xl shadow-sm relative overflow-hidden group">
                                                            {/* Provider icon watermark */}

                                                            <div className="absolute -right-4 -bottom-4 opacity-[0.03] pointer-events-none">
                                                                {acc.provider === 'zoom' ? (
                                                                    <svg className="w-32 h-32" fill="currentColor" viewBox="0 0 24 24"><path d="M4.585 13.607l-.27-.012H1.886l3.236-3.137L1.886 7.32l2.429-.012 2.226 2.144L8.767 7.32l2.43.012-3.236 3.138 3.236 3.137-2.43-.012-2.226-2.144-2.226 2.144h.27zM22.114 7.32l-2.43.012 3.236 3.138-3.236 3.137 2.43-.012 2.226-2.144L22.114 7.32zM15.42 15.353a4.015 4.015 0 01-3.42-1.928 4.015 4.015 0 01-3.42 1.928A4.04 4.04 0 014.54 11.31a4.04 4.04 0 014.04-4.042 4.015 4.015 0 013.42 1.928 4.015 4.015 0 013.42-1.928 4.04 4.04 0 014.04 4.042 4.04 4.04 0 01-4.04 4.043z"/></svg>
                                                                ) : (
                                                                    <svg className="w-32 h-32" fill="currentColor" viewBox="0 0 24 24"><path d="M21.196 4.954l-2.29-2.29a1.002 1.002 0 00-1.414 0l-1.393 1.393-5.26-5.26c-.39-.39-1.024-.39-1.414 0L1.758 6.464a1.002 1.002 0 000 1.414l5.26 5.26c.39.39 1.024.39 1.414 0l1.393-1.393 2.29 2.29c.39.39.39 1.024 0 1.414L3.81 23.754A1.002 1.002 0 003.81 25.17l2.29 2.29c.39.39 1.024.39 1.414 0l8.305-8.305c.39-.39.39-1.024 0-1.414l-2.29-2.29 1.393-1.393 5.26 5.26c.39.39 1.024.39 1.414 0l7.667-7.667a1.002 1.002 0 000-1.414z"/></svg>
                                                                )}

                                                            </div>
                                                            <div className="flex justify-between items-start mb-3">
                                                                <div>
                                                                    <div className="flex items-center gap-2 mb-1">
                                                                        <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded ${acc.provider === 'zoom' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                                                                            {acc.provider.replace('_', ' ')}

                                                                        </span>
                                                                        <span className={`flex items-center gap-1 text-[9px] font-bold ${acc.status === 'active' ? 'text-emerald-500' : 'text-red-500'}`}>
                                                                            <span className={`w-1.5 h-1.5 rounded-full ${acc.status === 'active' ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
                                                                            {acc.status}

                                                                        </span>
                                                                    </div>
                                                                    <h4 className="text-sm font-bold text-slate-800">{acc.account_label}</h4>
                                                                </div>
                                                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                                                    <button
                                                                        onClick={() => {
                                                                            setProviderFormData({
                                                                                provider: acc.provider || 'zoom',
                                                                                account_label: acc.account_label || '',
                                                                                email: acc.email || '',
                                                                                client_id: acc.client_id || '',
                                                                                client_secret: acc.client_secret || '',
                                                                                account_id: acc.account_id || '',
                                                                                api_key: acc.api_key || '',
                                                                                api_secret: acc.api_secret || '',
                                                                                refresh_token: acc.refresh_token || '',
                                                                                max_concurrent: acc.max_concurrent || 1,
                                                                                subscription_tier: acc.subscription_tier || 'pro',
                                                                                notes: acc.notes || ''
                                                                            });
                                                                            setEditingProviderId(acc.id);
                                                                            setShowProviderForm(true);
                                                                        }}
                                                                        className="p-1.5 text-slate-400 hover:text-blue-500 bg-slate-50 hover:bg-blue-50 rounded"
                                                                        title="Edit Provider Account"
                                                                    >
                                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleDeleteProviderAccount(acc.id)}

                                                                        className="p-1.5 text-slate-400 hover:text-red-500 bg-slate-50 hover:bg-red-50 rounded"
                                                                    >
                                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            <div className="space-y-1.5 text-[11px] text-slate-500 font-medium relative z-10">
                                                                <p className="flex items-center gap-2"><svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg> {acc.email || 'No email'}</p>
                                                                <p className="flex items-center gap-2"><svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg> {acc.max_concurrent} concurrent meeting(s)</p>
                                                                <p className="flex items-center gap-2"><svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg> {acc.subscription_tier} tier</p>
                                                            </div>
                                                            <div className="mt-4 pt-3 border-t border-slate-100 flex justify-between items-center relative z-10">
                                                                <span className="text-[9px] text-slate-400">Last used: {acc.last_used_at ? new Date(acc.last_used_at).toLocaleDateString() : 'Never'}</span>
                                                                <button
                                                                    onClick={() => handleTestProviderAccount(acc.id)}

                                                                    className="text-[10px] font-bold text-main hover:text-mainHover"
                                                                >
                                                                    Test Connection
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}

                                                </div>
                                            )}

                                        </section>
                                    </>
                                )}

                                {/* Test Result Toast */}

                                {providerTestResult && (
                                    <div className={`fixed top-6 right-6 p-4 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.2)] border flex items-start gap-3 z-[200] animate-fadeIn max-w-sm ${providerTestResult.valid ? 'bg-emerald-50/95 border-emerald-200 text-emerald-900' : 'bg-red-50/95 border-red-200 text-red-900'}`}>
                                        <span className={`flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full ${providerTestResult.valid ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                                            {providerTestResult.valid ? (
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                            ) : (
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                            )}

                                        </span>
                                        <div className="flex-grow pr-2">
                                            <h4 className={`text-xs font-black uppercase tracking-wider ${providerTestResult.valid ? 'text-emerald-800' : 'text-red-800'}`}>
                                                {providerTestResult.valid ? 'Connection Successful' : 'Connection Failed'}

                                            </h4>
                                            <p className="text-[11px] font-semibold mt-1 leading-snug break-words">{providerTestResult.message}</p>
                                        </div>
                                        <button 
                                            onClick={() => setProviderTestResult(null)}
                                            className="text-slate-400 hover:text-slate-600 font-black text-sm p-0.5"
                                        >
                                            ×
                                        </button>
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>
                )}

                {/* Resolve Escalation Modal */}
                {resolutionPopup.show && (
                    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[250] p-4 animate-fadeIn">
                        <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-slate-200 text-left space-y-5">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center text-red-600 shrink-0">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>
                                </div>
                                <div>
                                    <h3 className="text-base font-extrabold text-slate-900">Resolve Escalation</h3>
                                    <p className="text-xs font-semibold text-slate-400">Mark provider capacity alert as resolved</p>
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">RESOLUTION NOTES *</label>
                                <textarea
                                    rows={4}
                                    value={resolutionPopup.notes}
                                    onChange={(e) => setResolutionPopup(prev => ({ ...prev, notes: e.target.value }))}
                                    placeholder="Enter details on how the schedule capacity or conflict was resolved..."
                                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-xs font-medium text-slate-700 focus:bg-white focus:border-red-500 outline-none transition-all resize-none"
                                />
                            </div>

                            <div className="flex items-center gap-3 pt-1">
                                <button
                                    type="button"
                                    onClick={() => setResolutionPopup({ show: false, escalationId: null, notes: '' })}
                                    className="flex-1 py-3 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-2xl transition-all border border-slate-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={submitEscalationResolution}
                                    className="flex-1 py-3 px-4 bg-[#c41230] hover:bg-[#a00f27] text-white font-bold text-xs rounded-2xl transition-all shadow-md shadow-red-600/20"
                                >
                                    Resolve
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* View Attendance Log Modal */}
                {viewAttendanceModal.show && (() => {
                    const ATTENDANCE_PER_PAGE = Number(attendancePerPage) || 5;
                    const rawParts = viewAttendanceModal.participants || [];
                    const sortedParticipants = rawParts.length <= 1 ? rawParts : [...rawParts].sort((a, b) => {
                        if (attendanceSortBy === 'name') {
                            const nameA = (a.name || a.guest_email || '').toLowerCase();
                            const nameB = (b.name || b.guest_email || '').toLowerCase();
                            return nameA.localeCompare(nameB);
                        }
                        if (attendanceSortBy === 'status') {
                            const statusA = (a.join_status || 'invited').toLowerCase();
                            const statusB = (b.join_status || 'invited').toLowerCase();
                            return statusA.localeCompare(statusB);
                        }
                        // Default: 'host' -> Host first
                        const aIsHost = String(a.role || '').toLowerCase() === 'host';
                        const bIsHost = String(b.role || '').toLowerCase() === 'host';
                        if (aIsHost && !bIsHost) return -1;
                        if (!aIsHost && bIsHost) return 1;
                        return 0;
                    });
                    const totalParticipants = sortedParticipants.length;
                    const totalAttendancePages = Math.ceil(totalParticipants / ATTENDANCE_PER_PAGE) || 1;
                    const currentPage = Math.min(attendancePage, totalAttendancePages);
                    const paginatedParticipants = sortedParticipants.slice(
                        (currentPage - 1) * ATTENDANCE_PER_PAGE,
                        currentPage * ATTENDANCE_PER_PAGE
                    );

                    const isPastMeeting = viewAttendanceModal.meeting?.status === 'completed' || viewAttendanceModal.meeting?.status === 'cancelled';

                    return (
                        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[140] p-4 animate-fadeIn">
                            <div className="bg-white rounded-3xl p-6 max-w-lg w-full shadow-2xl border border-slate-200 text-left flex flex-col max-h-[85vh] overflow-hidden">
                                <div className="flex justify-between items-center border-b border-slate-100 pb-3 mb-4 shrink-0">
                                    <div>
                                        <h3 className="text-base font-extrabold text-slate-900">
                                            {viewAttendanceModal.meeting?.title}
                                        </h3>
                                        {viewAttendanceModal.meeting?.start_time && (
                                            <p className="text-xs font-medium text-slate-500 mt-0.5">
                                                {formatMeetingDate(viewAttendanceModal.meeting?.start_time)} • {formatStartTimeOnly(viewAttendanceModal.meeting?.start_time)}
                                            </p>
                                        )}
                                    </div>
                                    <button 
                                        onClick={() => setViewAttendanceModal({ show: false, meeting: null, participants: [] })}
                                        className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-100 rounded-full transition-colors"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                </div>

                                <div className="overflow-y-auto custom-scrollbar flex-grow space-y-3 pr-1">
                                    <div className="flex justify-between items-center px-1 mb-2 flex-wrap gap-2">
                                        <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">
                                            {(viewAttendanceModal.meeting?.status === 'completed' || viewAttendanceModal.meeting?.status === 'cancelled')
                                                ? `Who Attended (${totalParticipants} total)`
                                                : `Invitees (${totalParticipants} total)`}
                                        </span>
                                        
                                        {totalParticipants > 0 && (
                                            <div className="flex items-center gap-1 text-[10px] font-extrabold text-slate-400 uppercase tracking-wider ml-auto">
                                                <span>SHOW</span>
                                                <div className="relative inline-block">
                                                    <select
                                                        value={attendancePerPage}
                                                        onChange={(e) => {
                                                            setAttendancePerPage(Number(e.target.value));
                                                            setAttendancePage(1);
                                                        }}
                                                        className="appearance-none text-[10px] font-bold text-slate-700 bg-white border border-slate-300 rounded-full px-3 py-0.5 pr-6 outline-none focus:ring-1 focus:ring-[#c41230] cursor-pointer shadow-2xs"
                                                    >
                                                        <option value={5}>5</option>
                                                        <option value={10}>10</option>
                                                        <option value={25}>25</option>
                                                        <option value={50}>50</option>
                                                    </select>
                                                    <svg className="w-3 h-3 text-slate-500 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                                                </div>
                                                <span>ENTRIES</span>
                                            </div>
                                        )}
                                    </div>

                                    {totalParticipants === 0 ? (
                                        <div className="text-center py-8 text-slate-400 text-xs font-semibold">
                                            No attendance records logged for this meeting.
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {paginatedParticipants.map((p, idx) => (
                                                <div key={p.id || idx} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-2xl">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-9 h-9 rounded-full bg-[#c41230]/10 text-[#c41230] font-bold flex items-center justify-center text-xs uppercase shadow-sm shrink-0">
                                                            {(p.name || p.guest_email || 'G').charAt(0)}
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <h5 className="text-xs font-bold text-slate-800 truncate">
                                                                {p.name || p.guest_email || 'Participant'}
                                                            </h5>
                                                            <p className="text-[10px] text-slate-400 font-medium truncate">
                                                                {p.email || p.guest_email || 'No email provided'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col items-end gap-1 shrink-0">
                                                        {String(p.role || '').toLowerCase() === 'host' && (
                                                            <span className="px-2 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider bg-amber-50 text-amber-600 border border-amber-200">
                                                                HOST
                                                            </span>
                                                        )}
                                                        <span className={`text-[9px] font-bold ${
                                                            (isPastMeeting && (p.join_status === 'approved' || p.join_status === 'ended' || p.join_status === 'left')) ? 'text-emerald-600' : 'text-slate-400'
                                                        }`}>
                                                            {isPastMeeting
                                                                ? ((p.join_status === 'approved' || p.join_status === 'ended' || p.join_status === 'left') ? 'Attended Meeting' : 'Invited')
                                                                : (p.join_status && p.join_status !== 'approved' ? p.join_status : 'Invited')
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="pt-4 border-t border-slate-100 mt-4 shrink-0 flex items-center justify-between gap-3 flex-wrap">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        {totalParticipants > 0 && (
                                            <span className="text-[10px] font-bold text-slate-400">
                                                Showing {(currentPage - 1) * ATTENDANCE_PER_PAGE + 1}-{Math.min(currentPage * ATTENDANCE_PER_PAGE, totalParticipants)} of {totalParticipants}
                                            </span>
                                        )}
                                        {totalAttendancePages > 1 && (
                                            <div className="flex items-center gap-1.5">
                                                <button
                                                    disabled={currentPage === 1}
                                                    onClick={() => setAttendancePage(prev => Math.max(prev - 1, 1))}
                                                    className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                                >
                                                    Prev
                                                </button>
                                                <div className="flex items-center gap-1 px-1">
                                                    {Array.from({ length: totalAttendancePages }, (_, i) => i + 1).map(pageNum => (
                                                        <button
                                                            key={pageNum}
                                                            onClick={() => setAttendancePage(pageNum)}
                                                            className={`w-7 h-7 rounded-lg text-xs font-black transition-all ${
                                                                currentPage === pageNum
                                                                    ? 'bg-[#c41230] text-white shadow-xs'
                                                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                            }`}
                                                        >
                                                            {pageNum}
                                                        </button>
                                                    ))}
                                                </div>
                                                <button
                                                    disabled={currentPage === totalAttendancePages}
                                                    onClick={() => setAttendancePage(prev => Math.min(prev + 1, totalAttendancePages))}
                                                    className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 ml-auto">
                                        {totalParticipants > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const csvRows = [];
                                                    const companyName = "eNova Software and Hardware Solutions P Ltd.";
                                                    const mTitle = viewAttendanceModal.meeting?.title || 'Collaborative Meeting';
                                                    const rawStart = viewAttendanceModal.meeting?.start_time;
                                                    const mDate = rawStart ? new Date(rawStart).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB');

                                                    let mDuration = 'N/A';
                                                    if (rawStart) {
                                                        const startD = new Date(rawStart);
                                                        if (!isNaN(startD.getTime())) {
                                                            const endD = viewAttendanceModal.meeting?.end_time ? new Date(viewAttendanceModal.meeting.end_time) : new Date();
                                                            const diffMs = Math.max(0, endD - startD);
                                                            const diffMins = Math.floor(diffMs / 60000);
                                                            const hrs = Math.floor(diffMins / 60);
                                                            const mins = diffMins % 60;
                                                            mDuration = hrs > 0 ? `${hrs} hr${hrs > 1 ? 's' : ''} ${mins} min${mins !== 1 ? 's' : ''}` : `${mins} min${mins !== 1 ? 's' : ''}`;
                                                        }
                                                    }

                                                    csvRows.push([`"${companyName}"`]);
                                                    csvRows.push([`"Meeting Attendance Report - ${mTitle.replace(/"/g, '""')}"`]);
                                                    csvRows.push([`"Date: ${mDate} | Duration: ${mDuration} | Total Count: ${totalParticipants}"`]);
                                                    csvRows.push([]);

                                                    csvRows.push(['Name', 'Email', 'Join Time', 'End Time'].map(h => `"${h}"`).join(','));

                                                    (viewAttendanceModal.participants || []).forEach(p => {
                                                        const name = `"${(p.name || p.guest_email || p.email || 'Participant').replace(/"/g, '""')}"`;
                                                        const email = `"${(p.email || p.guest_email || 'N/A').replace(/"/g, '""')}"`;

                                                        let joinTimeStr = 'N/A';
                                                        const rawJoin = p.joined_at || p.join_time || p.created_at;
                                                        if (rawJoin) {
                                                            const jDate = new Date(rawJoin);
                                                            if (!isNaN(jDate.getTime())) {
                                                                joinTimeStr = jDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
                                                            } else {
                                                                joinTimeStr = String(rawJoin);
                                                            }
                                                        }

                                                        let endTimeStr = 'Active';
                                                        const rawEnd = p.left_at || p.end_time || p.leave_time;
                                                        if (rawEnd) {
                                                            const eDate = new Date(rawEnd);
                                                            if (!isNaN(eDate.getTime())) {
                                                                endTimeStr = eDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
                                                            } else {
                                                                endTimeStr = String(rawEnd);
                                                            }
                                                        } else if (p.join_status === 'left' || p.join_status === 'ended') {
                                                            endTimeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
                                                        }

                                                        csvRows.push([name, email, `"${joinTimeStr}"`, `"${endTimeStr}"`].join(','));
                                                    });

                                                    const csvString = csvRows.join('\n');
                                                    const blob = new Blob(['\uFEFF' + csvString], { type: 'text/csv;charset=utf-8;' });
                                                    const url = URL.createObjectURL(blob);
                                                    const link = document.createElement('a');
                                                    link.href = url;
                                                    link.setAttribute('download', `Meeting_Attendance_${viewAttendanceModal.meeting?.id || 'Report'}_${new Date().toISOString().split('T')[0]}.csv`);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                }}
                                                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-sm transition-all"
                                            >
                                                Export Excel
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setViewAttendanceModal({ show: false, meeting: null, participants: [] })}
                                            className="bg-[#c41230] hover:bg-[#a00f27] text-white font-bold px-6 py-2.5 rounded-xl transition-all text-xs shadow-md"
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </Layout>
        );
    };
    export default CollabMeetingDashboard;
//completed